// IndexedDB storage utility for scholarly book workflow
// Replaces localStorage to handle large manuscript files (up to 100 witnesses per chapter)

import type { LedgerSafeEditorialEvent } from '../types/ledgerSafeEditorialEvents'
import type {
  LedgerSafeActiveLedgerRecord,
  LedgerSafeLedgerRecord,
  LedgerSafeLedgerStateRecord,
} from '../types/ledgerSafeLedgers'
import type { LedgerSafeChapterSnapshot } from '../types/ledgerSafeSnapshots'
import type {
  ChapterDeviceLayer,
  DeviceAnnotationRunRecord,
  WitnessDeviceLayerRecord,
} from '../types/deviceAnnotationTypes'
import type { ChapterCharacterLayer } from '../types/characterLayerTypes'
import type { AuthorityWork, ManuscriptRecord } from '../types/authorityRecords'
import type { SourceObject } from '../types/sourceObjects'
import type {
  SourceCoordinateImportReview,
  SourceDocumentRecord,
  SourceSectionOccurrence,
} from '../types/sourceCoordinates'
import type { CrossTextTraditionLinkRecord } from '../types/crossTextTraditionLinks'
import type { WorkbenchProjectRecord } from '../types/workbenchProjects'
import type { WorkbenchWitnessAssignmentRecord } from '../types/workbenchWitnessAssignments'
import type { WitnessComparisonLayer, WitnessSourceMap } from '../types/workbenchWitnessLayers'
import type { TranslationPreviewResumeRecord, TranslationPreviewResumeSurface } from './translationPreviewResumePersistence'
import type { TextStructureLayer } from '../types/readerTextStructureTypes'
import type { ChapterStructuralRangeLayer } from '../types/structuralRangeTypes'
import type { AnnotationResearchNote } from '../types/annotationResearchNoteTypes'
import type { AnalyticalTaskSet } from '../types/analyticalTaskTypes'
import type { CameraLucidaPersistedInquiry } from '../types/cameraLucidaTypes'
import type {
  LedgerSafeAgentAtomicMutation,
  LedgerSafeAgentChangeSetRecord,
  LedgerSafeAuthorityHeadRecord,
} from '../types/ledgerSafeAgentChangeSets'
import { instrumentIndexedDBWriteTransactions } from './indexedDBSnapshotGuard'
import {
  assertLedgerSafePostgresPrimaryOperationSupported,
  indexedDbKeyFromRecord,
  isLedgerSafePostgresPrimaryCutoverRequired,
  runLedgerSafePostgresPrimaryWrite,
  type LedgerSafePrimaryRawMutation,
} from './ledgerSafePostgresPrimaryWriteBridge'
import {
  ensureLedgerSafePostgresPrimaryIndexCached,
  ensureLedgerSafePostgresPrimaryRecordCached,
  ensureLedgerSafePostgresPrimaryStoreCached,
  ensureLedgerSafePostgresPrimaryWitnessMetadataCached,
} from './ledgerSafePostgresPrimaryReadBridge'

export interface DBSchema {
  books: {
    id: string
    name: string
    arabic_title?: string
    authority_work_id?: string
    description?: string
    archived?: boolean
    archived_at?: number
    created_at: number
    last_modified: number
    chapter_order: string[]
    // Security enhancement: Add ownership checksum
    security_checksum?: string
  }
  chapters: {
    id: string
    book_id: string
    name: string
    label_prefix?: string
    description?: string
    authority_work_id?: string
    canonical_section_id?: string
    archived?: boolean
    archived_at?: number
    order_index: number
    created_at: number
    // Security enhancement: Add ownership validation
    security_checksum?: string
  }
  witnesses: {
    id: string
    chapter_id: string
    book_id: string  // SECURITY FIX: Add direct book_id reference for compound indexing
    source_object_id?: string
    sigla: string
    description: string
    manuscript_date?: string
    library: string
    uploaded_file?: any
    preprocessing_result?: any
    segmentation_result?: any
    segmentation_runs?: Record<string, any>
    active_segmentation_run?: string
    processing_status: 'pending' | 'preprocessing' | 'preprocessed' | 'segmenting' | 'auto-processing' | 'completed' | 'error' | 'paused'
    created_at: number
    last_modified: number  // SYNC: Track last modification for sync detection
    version: number  // SYNC: Version counter for change detection
    // Security enhancement: Add integrity monitoring
    security_checksum?: string
    data_integrity_hash?: string
  }
  witness_text: {
    witness_id: string
    schema_version: 1
    uploaded_file?: any
    preprocessing_result?: any
    source_witness_version: number
    updated_at: number
  }
  witness_segmentation: {
    witness_id: string
    schema_version: 1
    segmentation_result?: any
    segmentation_runs?: Record<string, any>
    active_segmentation_run?: string
    source_witness_version: number
    updated_at: number
  }
  source_objects: SourceObject
  source_documents: SourceDocumentRecord
  section_occurrences: SourceSectionOccurrence
  source_coordinate_import_reviews: SourceCoordinateImportReview
  manuscript_records: ManuscriptRecord
  authority_works: AuthorityWork
  ui_preferences: {
    key: string
    value: string
    updated_at: number
  }
  alignments: {
    id: string
    chapter_id: string
    book_id: string  // SECURITY FIX: Add direct book_id reference
    selected_witnesses: string[]
    alignment_result?: any
    created_at: number
    // Security enhancement: Add ownership validation
    security_checksum?: string
  }
  segmentation_baselines: {
    id: string
    witness_id: string
    chapter_id: string  // SECURITY FIX: Add chapter_id for proper scoping
    book_id: string     // SECURITY FIX: Add book_id for compound indexing
    baseline_hash: string
    segmentation_result: any
    metadata: {
      provider: string
      model: string
      processing_time: number
      total_units: number
      created_at: number
      security_signature?: string
    }
    is_current_baseline: boolean
    // Security enhancement: Add integrity monitoring
    security_checksum?: string
    data_integrity_hash?: string
  }
  edit_operations: {
    id: string
    session_id: string
    witness_id: string  // SECURITY FIX: Add witness_id for validation
    chapter_id: string  // SECURITY FIX: Add chapter_id for scoping
    book_id: string     // SECURITY FIX: Add book_id for compound indexing
    baseline_hash: string
    operation_type: 'merge' | 'split' | 'cross_chunk_merge'
    operation_data: any
    sequence_number: number
    timestamp: number
    applied_locally: boolean
    synced_to_server?: boolean
    // Security enhancement: Add integrity monitoring
    security_checksum?: string
  }
  processing_checkpoints: {
    id: string
    witness_id: string
    chapter_id: string  // SECURITY FIX: Add chapter_id for scoping
    book_id: string     // SECURITY FIX: Add book_id for compound indexing
    stage: 'preprocessing' | 'segmentation'
    progress: number
    checkpoint_data: any
    created_at: number
    can_resume: boolean
    // Security enhancement: Add integrity monitoring
    security_checksum?: string
  }
  witness_chunk_results: {
    id: string  // Format: witnessId_runId_chunkId
    witness_id: string
    run_id: string
    chunk_id: string
    result: any  // ChunkProcessingResult
    created_at: number
    last_updated: number
    // Emergency persistence tracking
    is_emergency_save: boolean
    sync_status: 'pending' | 'synced' | 'failed'
  }
  witness_comparison_layers: WitnessComparisonLayer
  witness_source_maps: WitnessSourceMap
  workbench_projects: WorkbenchProjectRecord
  workbench_witness_assignments: WorkbenchWitnessAssignmentRecord
  chapter_scenes: {
    chapter_id: string
    book_id: string
    version: number
    scene_order: string[]
    scene_map: Array<{
      scene_id: string
      title: string
      color?: string
      description?: string
      created_at: string
      updated_at?: string
      ui_metadata?: Record<string, any>
    }>
    canonical_anchors: Array<{
      scene_id: string
      reference_witness_id: string
      reference_unit_id?: string
      reference_chunk_id?: string
      reference_text_signature?: string
      reference_start_offset?: number
      reference_end_offset?: number
    }>
    metadata?: Record<string, any>
    created_at: string
    updated_at: string
  }
  witness_scenes: {
    witness_id: string
    chapter_id: string
    book_id: string
    canonical_chapter_version: number
    assignments: Array<{
      scene_id: string
      start_unit_id?: string
      end_unit_id?: string
      start_chunk_id: string
      end_chunk_id: string
      confidence?: number
      status: 'auto' | 'confirmed' | 'override'
      last_reviewed_at?: string
      notes?: string
    }>
    overrides?: Array<{
      scene_id: string
      title?: string
      color?: string
      extra?: boolean
      metadata?: Record<string, any>
    }>
    extras?: Array<{
      scene_id: string
      title: string
      start_unit_id?: string
      end_unit_id?: string
      start_chunk_id?: string
      end_chunk_id?: string
      created_at: string
    }>
    metadata?: Record<string, any>
    created_at: string
    updated_at: string
  }
  chapter_device_layers: ChapterDeviceLayer
  witness_device_layers: WitnessDeviceLayerRecord
  device_annotation_runs: DeviceAnnotationRunRecord
  chapter_character_layers: ChapterCharacterLayer
  reader_text_structure_layers: TextStructureLayer
  chapter_structural_layers: ChapterStructuralRangeLayer
  annotation_research_notes: AnnotationResearchNote
  analytical_task_sets: AnalyticalTaskSet
  canonical_alignments: {
    chapter_id: string
    book_id: string
    metadata: {
      created_at: string
      updated_at: string
      created_by?: string
      updated_by?: string
      chapter_prefix: string
      last_witness_update?: string
      row_count: number
      version: number
      extra?: Record<string, any>
    }
    rows: Array<{
      canonical_row_id: string
      order_index: number
      label?: string
      status: 'locked' | 'review' | 'provisional'
      created_at: string
      updated_at?: string
      notes?: string
      witness_mappings: Array<{
        witness_id: string
        unit_uuid: string
        unit_id?: string
        unit_label?: string
        chunk_id?: string
        confidence?: number
        status: 'confirmed' | 'auto' | 'pending'
        aligned_at?: string
        aligned_by?: string
        notes?: string
        metadata?: Record<string, any>
      }>
      metadata?: Record<string, any>
    }>
  }
  anchored_rows: {
    id: string                    // Row UUID
    chapterId: string             // FK to chapter
    labelPrefix: string           // User-editable prefix
    labelNumber: number           // Auto-generated, sequential
    position: number              // Sort order within chapter
    unitMappings: string          // JSON stringified UnitMapping[]
    createdAt: string             // ISO timestamp
    updatedAt: string             // ISO timestamp
    createdBy?: string            // User ID
    status: 'auto' | 'confirmed' | 'needs-review'
  }
  ledger_safe_edit_events: LedgerSafeEditorialEvent
  ledger_safe_snapshots: LedgerSafeChapterSnapshot
  ledger_safe_ledgers: LedgerSafeLedgerRecord
  ledger_safe_ledger_states: LedgerSafeLedgerStateRecord
  ledger_safe_active_ledgers: LedgerSafeActiveLedgerRecord
  cross_text_tradition_links: CrossTextTraditionLinkRecord
  translation_preview_resumes: TranslationPreviewResumeRecord
  camera_lucida_inquiries: CameraLucidaPersistedInquiry
  ledger_safe_agent_change_sets: LedgerSafeAgentChangeSetRecord
  ledger_safe_authority_heads: LedgerSafeAuthorityHeadRecord
}

export interface IndexedDBWriteObservation {
  store: keyof DBSchema
  records: number
  serializedCharacters: number
}

type IndexedDBWriteObserver = (observation: IndexedDBWriteObservation) => void

let indexedDBWriteObserver: IndexedDBWriteObserver | null = null

export const setIndexedDBWriteObserver = (observer: IndexedDBWriteObserver | null): void => {
  indexedDBWriteObserver = observer
}

const emitIndexedDBWriteObservation = (
  store: keyof DBSchema,
  records: DBSchema[keyof DBSchema][]
): void => {
  if (!indexedDBWriteObserver || records.length === 0) {
    return
  }
  indexedDBWriteObserver({
    store,
    records: records.length,
    serializedCharacters: JSON.stringify(records.length === 1 ? records[0] : records).length,
  })
}

export const PRIMARY_DB_STORE_NAMES: (keyof DBSchema)[] = [
  'books',
  'chapters',
  'witnesses',
  'witness_text',
  'witness_segmentation',
  'source_objects',
  'source_documents',
  'section_occurrences',
  'source_coordinate_import_reviews',
  'manuscript_records',
  'authority_works',
  'ui_preferences',
  'alignments',
  'segmentation_baselines',
  'edit_operations',
  'processing_checkpoints',
  'witness_chunk_results',
  'witness_comparison_layers',
  'witness_source_maps',
  'workbench_projects',
  'workbench_witness_assignments',
  'chapter_scenes',
  'witness_scenes',
  'chapter_device_layers',
  'witness_device_layers',
  'device_annotation_runs',
  'chapter_character_layers',
  'reader_text_structure_layers',
  'chapter_structural_layers',
  'annotation_research_notes',
  'analytical_task_sets',
  'canonical_alignments',
  'anchored_rows',
  'ledger_safe_edit_events',
  'ledger_safe_snapshots',
  'ledger_safe_ledgers',
  'ledger_safe_ledger_states',
  'ledger_safe_active_ledgers',
  'cross_text_tradition_links',
  'translation_preview_resumes',
  'camera_lucida_inquiries',
  'ledger_safe_agent_change_sets',
  'ledger_safe_authority_heads'
]

const estimateSerializedRecordCollectionSize = (records: unknown[]): number => {
  if (records.length === 0) {
    return 0
  }
  const serialized = JSON.stringify(records)
  const collectionOverheadBytes = records.length + 1
  return Math.max(0, new Blob([serialized]).size - collectionOverheadBytes)
}

export type IndexedDbStoreKeyPath = string | readonly string[]

/**
 * Authoritative logical keys used to validate a backup before any store is
 * cleared. Keep this catalog in lockstep with the createObjectStore calls
 * below; coverage is enforced by backup tests.
 */
export const PRIMARY_DB_STORE_KEY_PATHS: Record<keyof DBSchema, IndexedDbStoreKeyPath> = {
  books: 'id',
  chapters: 'id',
  witnesses: 'id',
  witness_text: 'witness_id',
  witness_segmentation: 'witness_id',
  source_objects: 'id',
  source_documents: 'id',
  section_occurrences: 'id',
  source_coordinate_import_reviews: 'id',
  manuscript_records: 'id',
  authority_works: 'id',
  ui_preferences: 'key',
  alignments: 'id',
  segmentation_baselines: 'id',
  edit_operations: 'id',
  processing_checkpoints: 'id',
  witness_chunk_results: 'id',
  witness_comparison_layers: 'id',
  witness_source_maps: 'id',
  workbench_projects: 'id',
  workbench_witness_assignments: 'id',
  chapter_scenes: 'chapter_id',
  witness_scenes: 'witness_id',
  chapter_device_layers: 'chapter_id',
  witness_device_layers: 'witness_id',
  device_annotation_runs: 'id',
  chapter_character_layers: 'chapter_id',
  reader_text_structure_layers: 'id',
  chapter_structural_layers: 'id',
  annotation_research_notes: 'id',
  analytical_task_sets: 'id',
  canonical_alignments: 'chapter_id',
  anchored_rows: 'id',
  ledger_safe_edit_events: 'id',
  ledger_safe_snapshots: 'id',
  ledger_safe_ledgers: 'id',
  ledger_safe_ledger_states: 'id',
  ledger_safe_active_ledgers: 'contextId',
  cross_text_tradition_links: 'id',
  translation_preview_resumes: 'id',
  camera_lucida_inquiries: 'id',
  ledger_safe_agent_change_sets: 'id',
  ledger_safe_authority_heads: 'chapterId',
}

// ============================================================================
// Security Enhancement Interfaces
// ============================================================================

export interface OwnershipContext {
  book_id: string
  chapter_id?: string
  witness_id?: string
}

export interface SecurityValidationResult {
  isValid: boolean
  violations: string[]
  context: OwnershipContext
}

export interface IntegrityCheckResult {
  isIntact: boolean
  corrupted_items: string[]
  checksum_mismatches: number
}

export class LedgerSafeAgentAtomicConflictError extends Error {
  constructor(message = 'The stored Ledger Safe authority changed before this agent sequence could be committed.') {
    super(message)
    this.name = 'LedgerSafeAgentAtomicConflictError'
  }
}

const ledgerSafeAtomicValuesMatch = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right)

const serializeLedgerSafeAtomicRow = (row: LedgerSafeAgentAtomicMutation['nextState']['rows'][number]) => ({
  ...row,
  unitMappings: JSON.stringify(row.unitMappings),
})

const buildWitnessTextPayloadRecord = (
  witness: DBSchema['witnesses']
): DBSchema['witness_text'] | null => {
  if (witness.uploaded_file === undefined && witness.preprocessing_result === undefined) {
    return null
  }
  return {
    witness_id: witness.id,
    schema_version: 1,
    uploaded_file: witness.uploaded_file,
    preprocessing_result: witness.preprocessing_result,
    source_witness_version: witness.version || 0,
    updated_at: witness.last_modified || Date.now(),
  }
}

const hasWitnessTextPayloadFields = (witness: Partial<DBSchema['witnesses']>): boolean => (
  Object.prototype.hasOwnProperty.call(witness, 'uploaded_file')
  || Object.prototype.hasOwnProperty.call(witness, 'preprocessing_result')
)

const hasWitnessSegmentationPayloadFields = (witness: Partial<DBSchema['witnesses']>): boolean => (
  Object.prototype.hasOwnProperty.call(witness, 'segmentation_result')
  || Object.prototype.hasOwnProperty.call(witness, 'segmentation_runs')
  || Object.prototype.hasOwnProperty.call(witness, 'active_segmentation_run')
)

const buildWitnessSegmentationPayloadRecord = (
  witness: DBSchema['witnesses']
): DBSchema['witness_segmentation'] | null => {
  if (
    witness.segmentation_result === undefined
    && witness.segmentation_runs === undefined
    && witness.active_segmentation_run === undefined
  ) {
    return null
  }
  return {
    witness_id: witness.id,
    schema_version: 1,
    segmentation_result: witness.segmentation_result,
    segmentation_runs: witness.segmentation_runs,
    active_segmentation_run: witness.active_segmentation_run,
    source_witness_version: witness.version || 0,
    updated_at: witness.last_modified || Date.now(),
  }
}

export const PRIMARY_DB_NAME = 'ScholarlyWorkflowDB'
export const PRIMARY_DB_VERSION = 30

export type IndexedDBRecordWrites = Partial<{
  [K in keyof DBSchema]: DBSchema[K][]
}>

export type IndexedDBRecordDeletes = Partial<{
  [K in keyof DBSchema]: IDBValidKey[]
}>

export interface IndexedDBRecordChanges {
  puts?: IndexedDBRecordWrites
  deletes?: IndexedDBRecordDeletes
}

export class IndexedDBStorage {
  private db: IDBDatabase | null = null
  private initPromise: Promise<void> | null = null
  private securityEnabled = true  // Enable security validation by default

  constructor(
    private readonly dbName = PRIMARY_DB_NAME,
    private readonly version = PRIMARY_DB_VERSION,
  ) {}

  async init(): Promise<void> {
    if (this.db) {
      return
    }
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        instrumentIndexedDBWriteTransactions(this.db, this.dbName)
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create object stores
        if (!db.objectStoreNames.contains('books')) {
          db.createObjectStore('books', { keyPath: 'id' })
        }

        if (!db.objectStoreNames.contains('chapters')) {
          const chapterStore = db.createObjectStore('chapters', { keyPath: 'id' })
          chapterStore.createIndex('book_id', 'book_id', { unique: false })
          // SECURITY FIX: Add compound index for ownership validation
          chapterStore.createIndex('book_security', ['book_id', 'security_checksum'], { unique: false })
        }

        if (!db.objectStoreNames.contains('witnesses')) {
          const witnessStore = db.createObjectStore('witnesses', { keyPath: 'id' })
          witnessStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          // SECURITY FIX: Add compound indexes for complete isolation
          witnessStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'id'], { unique: true })
          witnessStore.createIndex('book_id', 'book_id', { unique: false })
          witnessStore.createIndex('security_validation', ['book_id', 'chapter_id', 'security_checksum'], { unique: false })
          witnessStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
          witnessStore.createIndex('source_object_id', 'source_object_id', { unique: false })
        } else {
          const witnessStore = request.transaction?.objectStore('witnesses')
          if (witnessStore && !witnessStore.indexNames.contains('source_object_id')) {
            witnessStore.createIndex('source_object_id', 'source_object_id', { unique: false })
          }
        }

        // Version 30: additive witness payload stores. The legacy payload
        // fields remain on witnesses during the compatibility period; reads
        // can therefore fall back safely and backfill these stores lazily.
        if (!db.objectStoreNames.contains('witness_text')) {
          const witnessTextStore = db.createObjectStore('witness_text', { keyPath: 'witness_id' })
          witnessTextStore.createIndex('source_witness_version', 'source_witness_version', { unique: false })
          witnessTextStore.createIndex('updated_at', 'updated_at', { unique: false })
        }

        if (!db.objectStoreNames.contains('witness_segmentation')) {
          const witnessSegmentationStore = db.createObjectStore('witness_segmentation', { keyPath: 'witness_id' })
          witnessSegmentationStore.createIndex('source_witness_version', 'source_witness_version', { unique: false })
          witnessSegmentationStore.createIndex('updated_at', 'updated_at', { unique: false })
        }

        if (!db.objectStoreNames.contains('source_objects')) {
          const sourceObjectStore = db.createObjectStore('source_objects', { keyPath: 'id' })
          sourceObjectStore.createIndex('work_id', 'work_id', { unique: false })
          sourceObjectStore.createIndex('normalized_match_key', 'normalized_match_key', { unique: false })
          sourceObjectStore.createIndex('work_match_key', ['work_id', 'normalized_match_key'], { unique: false })
          sourceObjectStore.createIndex('canonical_label', 'canonical_label', { unique: false })
        }

        if (!db.objectStoreNames.contains('source_documents')) {
          const sourceDocumentStore = db.createObjectStore('source_documents', { keyPath: 'id' })
          sourceDocumentStore.createIndex('source_object_id', 'source_object_id', { unique: false })
          sourceDocumentStore.createIndex('work_id', 'work_id', { unique: false })
          sourceDocumentStore.createIndex('manuscript_record_id', 'manuscript_record_id', { unique: false })
          sourceDocumentStore.createIndex('source_map_id', 'source_map_id', { unique: false })
          sourceDocumentStore.createIndex('raw_text_hash', 'raw_text_hash', { unique: false })
          sourceDocumentStore.createIndex('review_status', 'review_status', { unique: false })
          sourceDocumentStore.createIndex('last_modified', 'last_modified', { unique: false })
        }

        if (!db.objectStoreNames.contains('section_occurrences')) {
          const occurrenceStore = db.createObjectStore('section_occurrences', { keyPath: 'id' })
          occurrenceStore.createIndex('authority_work_id', 'authority_work_id', { unique: false })
          occurrenceStore.createIndex('authority_section_id', 'authority_section_id', { unique: false })
          occurrenceStore.createIndex('source_object_id', 'source_object_id', { unique: false })
          occurrenceStore.createIndex('source_document_id', 'source_document_id', { unique: false })
          occurrenceStore.createIndex('witness_id', 'witness_id', { unique: false })
          occurrenceStore.createIndex('source_map_id', 'source_map_id', { unique: false })
          occurrenceStore.createIndex('raw_text_hash', 'raw_text_hash', { unique: false })
          occurrenceStore.createIndex('work_section', ['authority_work_id', 'authority_section_id'], { unique: false })
          occurrenceStore.createIndex('source_section', ['source_object_id', 'authority_section_id'], { unique: false })
          occurrenceStore.createIndex('review_status', 'review_status', { unique: false })
        }

        if (!db.objectStoreNames.contains('source_coordinate_import_reviews')) {
          const reviewStore = db.createObjectStore('source_coordinate_import_reviews', { keyPath: 'id' })
          reviewStore.createIndex('status', 'status', { unique: false })
          reviewStore.createIndex('source', 'source', { unique: false })
          reviewStore.createIndex('created_at', 'created_at', { unique: false })
          reviewStore.createIndex('last_modified', 'last_modified', { unique: false })
        }

        // Version 21: codex-level authority records. Purely additive — no
        // existing store is touched by this upgrade.
        if (!db.objectStoreNames.contains('manuscript_records')) {
          const manuscriptStore = db.createObjectStore('manuscript_records', { keyPath: 'id' })
          manuscriptStore.createIndex('normalized_match_key', 'normalized_match_key', { unique: false })
          manuscriptStore.createIndex('iiif_manifest_url', 'iiif_manifest_url', { unique: false })
          manuscriptStore.createIndex('canonical_label', 'canonical_label', { unique: false })
        }

        if (!db.objectStoreNames.contains('authority_works')) {
          const authorityWorkStore = db.createObjectStore('authority_works', { keyPath: 'id' })
          authorityWorkStore.createIndex('title', 'title', { unique: false })
        }

        if (!db.objectStoreNames.contains('ui_preferences')) {
          const uiPreferenceStore = db.createObjectStore('ui_preferences', { keyPath: 'key' })
          uiPreferenceStore.createIndex('updated_at', 'updated_at', { unique: false })
        }

        if (!db.objectStoreNames.contains('alignments')) {
          const alignmentStore = db.createObjectStore('alignments', { keyPath: 'id' })
          alignmentStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          // SECURITY FIX: Add compound indexes for proper isolation
          alignmentStore.createIndex('book_chapter', ['book_id', 'chapter_id'], { unique: false })
          alignmentStore.createIndex('book_id', 'book_id', { unique: false })
        }

        // New stores for hybrid storage architecture with SECURITY ENHANCEMENTS
        if (!db.objectStoreNames.contains('segmentation_baselines')) {
          const baselineStore = db.createObjectStore('segmentation_baselines', { keyPath: 'id' })
          baselineStore.createIndex('witness_id', 'witness_id', { unique: false })
          baselineStore.createIndex('baseline_hash', 'baseline_hash', { unique: true })
          baselineStore.createIndex('witness_current', ['witness_id', 'is_current_baseline'], { unique: false })
          // SECURITY FIX: Add compound indexes for complete isolation
          baselineStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'witness_id'], { unique: false })
          baselineStore.createIndex('security_validation', ['book_id', 'chapter_id', 'witness_id', 'security_checksum'], { unique: false })
          baselineStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
          baselineStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          baselineStore.createIndex('book_id', 'book_id', { unique: false })
        }

        if (!db.objectStoreNames.contains('edit_operations')) {
          const operationStore = db.createObjectStore('edit_operations', { keyPath: 'id' })
          operationStore.createIndex('session_id', 'session_id', { unique: false })
          operationStore.createIndex('baseline_hash', 'baseline_hash', { unique: false })
          operationStore.createIndex('session_sequence', ['session_id', 'sequence_number'], { unique: true })
          operationStore.createIndex('sync_status', 'synced_to_server', { unique: false })
          // SECURITY FIX: Add compound indexes for complete isolation
          operationStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'witness_id'], { unique: false })
          operationStore.createIndex('security_validation', ['book_id', 'chapter_id', 'witness_id', 'session_id'], { unique: false })
          operationStore.createIndex('witness_id', 'witness_id', { unique: false })
          operationStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          operationStore.createIndex('book_id', 'book_id', { unique: false })
        }

        if (!db.objectStoreNames.contains('processing_checkpoints')) {
          const checkpointStore = db.createObjectStore('processing_checkpoints', { keyPath: 'id' })
          checkpointStore.createIndex('witness_id', 'witness_id', { unique: false })
          checkpointStore.createIndex('witness_stage', ['witness_id', 'stage'], { unique: false })
          // SECURITY FIX: Add compound indexes for complete isolation
          checkpointStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'witness_id'], { unique: false })
          checkpointStore.createIndex('security_validation', ['book_id', 'chapter_id', 'witness_id', 'stage'], { unique: false })
          checkpointStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          checkpointStore.createIndex('book_id', 'book_id', { unique: false })
        }
        // EMERGENCY PERSISTENCE: Add witness_chunk_results for data loss prevention
        if (!db.objectStoreNames.contains('witness_chunk_results')) {
          const chunkResultStore = db.createObjectStore('witness_chunk_results', { keyPath: 'id' })
          chunkResultStore.createIndex('witness_id', 'witness_id', { unique: false })
          chunkResultStore.createIndex('run_id', 'run_id', { unique: false })
          chunkResultStore.createIndex('witness_run', ['witness_id', 'run_id'], { unique: false })
          chunkResultStore.createIndex('witness_run_chunk', ['witness_id', 'run_id', 'chunk_id'], { unique: true })
          chunkResultStore.createIndex('emergency_saves', 'is_emergency_save', { unique: false })
          chunkResultStore.createIndex('sync_status', 'sync_status', { unique: false })
          chunkResultStore.createIndex('last_updated', 'last_updated', { unique: false })
        }

        if (!db.objectStoreNames.contains('witness_comparison_layers')) {
          const comparisonLayerStore = db.createObjectStore('witness_comparison_layers', { keyPath: 'id' })
          comparisonLayerStore.createIndex('witness_id', 'witness_id', { unique: false })
          comparisonLayerStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'witness_id'], { unique: false })
          comparisonLayerStore.createIndex('witness_current', ['witness_id', 'is_active'], { unique: false })
          comparisonLayerStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          comparisonLayerStore.createIndex('book_id', 'book_id', { unique: false })
          comparisonLayerStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
        }

        if (!db.objectStoreNames.contains('witness_source_maps')) {
          const sourceMapStore = db.createObjectStore('witness_source_maps', { keyPath: 'id' })
          sourceMapStore.createIndex('witness_id', 'witness_id', { unique: false })
          sourceMapStore.createIndex('book_chapter_witness', ['book_id', 'chapter_id', 'witness_id'], { unique: false })
          sourceMapStore.createIndex('witness_current', ['witness_id', 'is_active'], { unique: false })
          sourceMapStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          sourceMapStore.createIndex('book_id', 'book_id', { unique: false })
          sourceMapStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
        }

        if (!db.objectStoreNames.contains('workbench_projects')) {
          const projectStore = db.createObjectStore('workbench_projects', { keyPath: 'id' })
          projectStore.createIndex('last_modified', 'last_modified', { unique: false })
          projectStore.createIndex('source_book_ids', 'source_book_ids', { unique: false, multiEntry: true })
          projectStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
        }

        if (!db.objectStoreNames.contains('workbench_witness_assignments')) {
          const assignmentStore = db.createObjectStore('workbench_witness_assignments', { keyPath: 'id' })
          assignmentStore.createIndex('project_id', 'project_id', { unique: false })
          assignmentStore.createIndex('witness_id', 'witness_id', { unique: false })
          assignmentStore.createIndex('project_witness', ['project_id', 'witness_id'], { unique: true })
          assignmentStore.createIndex('context_id', 'context_id', { unique: false })
          assignmentStore.createIndex('work_id', 'work_id', { unique: false })
          assignmentStore.createIndex('status', 'status', { unique: false })
          assignmentStore.createIndex('integrity_check', 'data_integrity_hash', { unique: false })
        }

        if (!db.objectStoreNames.contains('chapter_scenes')) {
          const chapterScenesStore = db.createObjectStore('chapter_scenes', { keyPath: 'chapter_id' })
          chapterScenesStore.createIndex('book_id', 'book_id', { unique: false })
          chapterScenesStore.createIndex('version', 'version', { unique: false })
        }

        if (!db.objectStoreNames.contains('witness_scenes')) {
          const witnessScenesStore = db.createObjectStore('witness_scenes', { keyPath: 'witness_id' })
          witnessScenesStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          witnessScenesStore.createIndex('book_id', 'book_id', { unique: false })
          witnessScenesStore.createIndex('canonical_version', 'canonical_chapter_version', { unique: false })
        }

        if (!db.objectStoreNames.contains('chapter_device_layers')) {
          const chapterDeviceStore = db.createObjectStore('chapter_device_layers', { keyPath: 'chapter_id' })
          chapterDeviceStore.createIndex('book_id', 'book_id', { unique: false })
          chapterDeviceStore.createIndex('version', 'version', { unique: false })
          chapterDeviceStore.createIndex('taxonomy_version', 'taxonomy_version', { unique: false })
        }

        if (!db.objectStoreNames.contains('witness_device_layers')) {
          const witnessDeviceStore = db.createObjectStore('witness_device_layers', { keyPath: 'witness_id' })
          witnessDeviceStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          witnessDeviceStore.createIndex('book_id', 'book_id', { unique: false })
          witnessDeviceStore.createIndex('chapter_device_version', 'chapter_device_version', { unique: false })
          witnessDeviceStore.createIndex('taxonomy_version', 'taxonomy_version', { unique: false })
        }

        if (!db.objectStoreNames.contains('device_annotation_runs')) {
          const runStore = db.createObjectStore('device_annotation_runs', { keyPath: 'id' })
          runStore.createIndex('chapter_id', 'chapter_id', { unique: false })
          runStore.createIndex('book_id', 'book_id', { unique: false })
          runStore.createIndex('mode', 'mode', { unique: false })
          runStore.createIndex('status', 'status', { unique: false })
          runStore.createIndex('chapter_mode', ['chapter_id', 'mode'], { unique: false })
        }

        if (!db.objectStoreNames.contains('chapter_character_layers')) {
          const characterLayerStore = db.createObjectStore('chapter_character_layers', { keyPath: 'chapter_id' })
          characterLayerStore.createIndex('book_id', 'book_id', { unique: false })
          characterLayerStore.createIndex('version', 'version', { unique: false })
          characterLayerStore.createIndex('updated_at', 'updated_at', { unique: false })
        }

        if (!db.objectStoreNames.contains('reader_text_structure_layers')) {
          const structureStore = db.createObjectStore('reader_text_structure_layers', { keyPath: 'id' })
          structureStore.createIndex('chapterId', 'chapterId', { unique: false })
          structureStore.createIndex('sourceWitnessId', 'sourceWitnessId', { unique: false })
          structureStore.createIndex('chapter_source', ['chapterId', 'sourceWitnessId'], { unique: true })
          structureStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        // Version 29: chapter-wide, hierarchy-aware structural ranges. This is
        // additive; legacy scene metadata remains available for explicit review.
        if (!db.objectStoreNames.contains('chapter_structural_layers')) {
          const structuralRangeStore = db.createObjectStore('chapter_structural_layers', { keyPath: 'id' })
          structuralRangeStore.createIndex('chapterId', 'chapterId', { unique: true })
          structuralRangeStore.createIndex('bookId', 'bookId', { unique: false })
          structuralRangeStore.createIndex('version', 'version', { unique: false })
          structuralRangeStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        if (!db.objectStoreNames.contains('annotation_research_notes')) {
          const noteStore = db.createObjectStore('annotation_research_notes', { keyPath: 'id' })
          noteStore.createIndex('chapterId', 'chapterId', { unique: false })
          noteStore.createIndex('sourceWitnessId', 'sourceWitnessId', { unique: false })
          noteStore.createIndex('targetKind', 'target.kind', { unique: false })
          noteStore.createIndex('targetAnnotationId', 'target.annotationId', { unique: false })
          noteStore.createIndex('logicalTargetId', 'target.logicalId', { unique: false })
          noteStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          noteStore.createIndex('chapter_source', ['chapterId', 'sourceWitnessId'], { unique: false })
        }

        if (!db.objectStoreNames.contains('analytical_task_sets')) {
          const taskSetStore = db.createObjectStore('analytical_task_sets', { keyPath: 'id' })
          taskSetStore.createIndex('productKind', 'productKind', { unique: false })
          taskSetStore.createIndex('workIds', 'workIds', { unique: false, multiEntry: true })
          taskSetStore.createIndex('chapterIds', 'chapterIds', { unique: false, multiEntry: true })
          taskSetStore.createIndex('witnessIds', 'witnessIds', { unique: false, multiEntry: true })
          taskSetStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        if (!db.objectStoreNames.contains('canonical_alignments')) {
          const canonicalStore = db.createObjectStore('canonical_alignments', { keyPath: 'chapter_id' })
          canonicalStore.createIndex('book_id', 'book_id', { unique: false })
          canonicalStore.createIndex('version', 'metadata.version', { unique: false })
        }

        // Anchored Rows Store - authoritative alignment registry
        if (!db.objectStoreNames.contains('anchored_rows')) {
          const anchoredStore = db.createObjectStore('anchored_rows', { keyPath: 'id' })
          anchoredStore.createIndex('chapterId', 'chapterId', { unique: false })
          anchoredStore.createIndex('position', 'position', { unique: false })
          anchoredStore.createIndex('chapter_position', ['chapterId', 'position'], { unique: false })
          anchoredStore.createIndex('status', 'status', { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_edit_events')) {
          const editorialEventStore = db.createObjectStore('ledger_safe_edit_events', { keyPath: 'id' })
          editorialEventStore.createIndex('chapterId', 'chapterId', { unique: false })
          editorialEventStore.createIndex('createdAt', 'createdAt', { unique: false })
          editorialEventStore.createIndex('chapter_createdAt', ['chapterId', 'createdAt'], { unique: false })
          editorialEventStore.createIndex('source', 'source', { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_snapshots')) {
          const snapshotStore = db.createObjectStore('ledger_safe_snapshots', { keyPath: 'id' })
          snapshotStore.createIndex('chapterId', 'chapterId', { unique: false })
          snapshotStore.createIndex('scopeId', 'scopeId', { unique: false })
          snapshotStore.createIndex('createdAt', 'createdAt', { unique: false })
          snapshotStore.createIndex('reason', 'reason', { unique: false })
          snapshotStore.createIndex('chapter_createdAt', ['chapterId', 'createdAt'], { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_ledgers')) {
          const ledgerStore = db.createObjectStore('ledger_safe_ledgers', { keyPath: 'id' })
          ledgerStore.createIndex('contextId', 'contextId', { unique: false })
          ledgerStore.createIndex('createdAt', 'createdAt', { unique: false })
          ledgerStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          ledgerStore.createIndex('context_updatedAt', ['contextId', 'updatedAt'], { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_ledger_states')) {
          const ledgerStateStore = db.createObjectStore('ledger_safe_ledger_states', { keyPath: 'id' })
          ledgerStateStore.createIndex('ledgerId', 'ledgerId', { unique: false })
          ledgerStateStore.createIndex('contextId', 'contextId', { unique: false })
          ledgerStateStore.createIndex('kind', 'kind', { unique: false })
          ledgerStateStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          ledgerStateStore.createIndex('ledger_updatedAt', ['ledgerId', 'updatedAt'], { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_active_ledgers')) {
          const activeLedgerStore = db.createObjectStore('ledger_safe_active_ledgers', { keyPath: 'contextId' })
          activeLedgerStore.createIndex('ledgerId', 'ledgerId', { unique: false })
          activeLedgerStore.createIndex('stateId', 'stateId', { unique: false })
          activeLedgerStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        if (!db.objectStoreNames.contains('cross_text_tradition_links')) {
          const crossTextLinkStore = db.createObjectStore('cross_text_tradition_links', { keyPath: 'id' })
          crossTextLinkStore.createIndex('scopeId', 'scopeId', { unique: false })
          crossTextLinkStore.createIndex('previewId', 'previewId', { unique: false })
          crossTextLinkStore.createIndex('candidateId', 'candidateId', { unique: false })
          crossTextLinkStore.createIndex('reviewStatus', 'reviewStatus', { unique: false })
          crossTextLinkStore.createIndex('sourceTextId', 'source.textId', { unique: false })
          crossTextLinkStore.createIndex('targetTextId', 'target.textId', { unique: false })
          crossTextLinkStore.createIndex('createdAt', 'createdAt', { unique: false })
        }

        if (!db.objectStoreNames.contains('translation_preview_resumes')) {
          const translationResumeStore = db.createObjectStore('translation_preview_resumes', { keyPath: 'id' })
          translationResumeStore.createIndex('surface', 'surface', { unique: false })
          translationResumeStore.createIndex('contextId', 'contextId', { unique: false })
          translationResumeStore.createIndex('witnessId', 'witnessId', { unique: false })
          translationResumeStore.createIndex('surface_context', ['surface', 'contextId'], { unique: false })
          translationResumeStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }

        if (!db.objectStoreNames.contains('camera_lucida_inquiries')) {
          const cameraInquiryStore = db.createObjectStore('camera_lucida_inquiries', { keyPath: 'id' })
          cameraInquiryStore.createIndex('chapterId', 'chapterId', { unique: false })
          cameraInquiryStore.createIndex('bookId', 'bookId', { unique: false })
          cameraInquiryStore.createIndex('completedAt', 'completedAt', { unique: false })
          cameraInquiryStore.createIndex('chapter_completedAt', ['chapterId', 'completedAt'], { unique: false })
        }

        // Version 28: durable, approval-required agent edit sequences. This is
        // additive: no existing store or record is rewritten during upgrade.
        if (!db.objectStoreNames.contains('ledger_safe_agent_change_sets')) {
          const changeSetStore = db.createObjectStore('ledger_safe_agent_change_sets', { keyPath: 'id' })
          changeSetStore.createIndex('chapterId', 'chapterId', { unique: false })
          changeSetStore.createIndex('bookId', 'bookId', { unique: false })
          changeSetStore.createIndex('status', 'status', { unique: false })
          changeSetStore.createIndex('agentSessionId', 'agentSessionId', { unique: false })
          changeSetStore.createIndex('createdAt', 'createdAt', { unique: false })
          changeSetStore.createIndex('chapter_createdAt', ['chapterId', 'createdAt'], { unique: false })
        }

        if (!db.objectStoreNames.contains('ledger_safe_authority_heads')) {
          const authorityHeadStore = db.createObjectStore('ledger_safe_authority_heads', { keyPath: 'chapterId' })
          authorityHeadStore.createIndex('bookId', 'bookId', { unique: false })
          authorityHeadStore.createIndex('authorityRevisionId', 'authorityRevisionId', { unique: true })
          authorityHeadStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }
      }
    }).finally(() => {
      if (!this.db) {
        this.initPromise = null
      }
    })

    return this.initPromise
  }

  async ready(): Promise<void> {
    if (this.db) {
      return
    }
    await this.init()
  }

  close(): void {
    this.db?.close()
    this.db = null
    this.initPromise = null
  }

  private ensureDB(): IDBDatabase {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.')
    }
    return this.db
  }

  private async hydrateWitnessRecord(
    witness: DBSchema['witnesses']
  ): Promise<DBSchema['witnesses']> {
    const [textPayload, segmentationPayload] = await Promise.all([
      this.get('witness_text', witness.id),
      this.get('witness_segmentation', witness.id),
    ])
    const missingTextPayload = !textPayload
      && (witness.uploaded_file !== undefined || witness.preprocessing_result !== undefined)
    const missingSegmentationPayload = !segmentationPayload
      && (
        witness.segmentation_result !== undefined
        || witness.segmentation_runs !== undefined
        || witness.active_segmentation_run !== undefined
      )

    if (missingTextPayload || missingSegmentationPayload) {
      const textRecord = missingTextPayload ? buildWitnessTextPayloadRecord(witness) : null
      const segmentationRecord = missingSegmentationPayload
        ? buildWitnessSegmentationPayloadRecord(witness)
        : null
      await this.putManyAcrossStores({
        witness_text: textRecord ? [textRecord] : [],
        witness_segmentation: segmentationRecord ? [segmentationRecord] : [],
      })
    }

    return {
      ...witness,
      ...(textPayload
        ? {
            uploaded_file: textPayload.uploaded_file,
            preprocessing_result: textPayload.preprocessing_result,
          }
        : {}),
      ...(segmentationPayload
        ? {
            segmentation_result: segmentationPayload.segmentation_result,
            segmentation_runs: segmentationPayload.segmentation_runs,
            active_segmentation_run: segmentationPayload.active_segmentation_run,
          }
        : {}),
    }
  }

  private async hydrateWitnessRecords(
    witnesses: DBSchema['witnesses'][]
  ): Promise<DBSchema['witnesses'][]> {
    return Promise.all(witnesses.map(witness => this.hydrateWitnessRecord(witness)))
  }

  /**
   * Metadata-only witness scan for route-level inventory. Payloads are loaded
   * when a chapter or witness is opened through the regular read APIs.
   */
  async getAllWitnessMetadata(): Promise<DBSchema['witnesses'][]> {
    await this.ready()
    await ensureLedgerSafePostgresPrimaryWitnessMetadataCached()
    const db = this.ensureDB()
    const transaction = db.transaction(['witnesses'], 'readonly')
    const request = transaction.objectStore('witnesses').getAll()
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result.map((witness: DBSchema['witnesses']) => {
        const {
          uploaded_file: _uploadedFile,
          preprocessing_result: _preprocessingResult,
          segmentation_result: _segmentationResult,
          segmentation_runs: _segmentationRuns,
          ...metadata
        } = witness
        return metadata
      }))
      request.onerror = () => reject(request.error)
    })
  }

  // Generic CRUD operations
  async get<T extends keyof DBSchema>(store: T, id: string): Promise<DBSchema[T] | undefined> {
    await this.ready()
    const db = this.ensureDB()
    const readRecord = (): Promise<DBSchema[T] | undefined> => {
      const transaction = db.transaction([store], 'readonly')
      const objectStore = transaction.objectStore(store)
      return new Promise((resolve, reject) => {
        const request = objectStore.get(id)
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
    }
    let record = await readRecord()
    if (record === undefined) {
      await ensureLedgerSafePostgresPrimaryRecordCached({
        databaseName: PRIMARY_DB_NAME,
        storeName: String(store),
        key: id,
      })
      record = await readRecord()
    }
    if (store === 'witnesses' && record) {
      return this.hydrateWitnessRecord(record as DBSchema['witnesses']) as Promise<DBSchema[T]>
    }
    return record
  }

  async getAll<T extends keyof DBSchema>(store: T): Promise<DBSchema[T][]> {
    await this.ready()
    await ensureLedgerSafePostgresPrimaryStoreCached({
      databaseName: PRIMARY_DB_NAME,
      storeName: String(store),
    })
    const db = this.ensureDB()
    const transaction = db.transaction([store], 'readonly')
    const objectStore = transaction.objectStore(store)

    const records = await new Promise<DBSchema[T][]>((resolve, reject) => {
      const request = objectStore.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    if (store === 'witnesses') {
      return this.hydrateWitnessRecords(records as DBSchema['witnesses'][]) as Promise<DBSchema[T][]>
    }
    return records
  }

  async getAllByIndex<T extends keyof DBSchema>(
    store: T,
    indexName: string,
    value: string
  ): Promise<DBSchema[T][]> {
    await this.ready()
    const db = this.ensureDB()
    const keyPathTransaction = db.transaction([store], 'readonly')
    const rawKeyPath = keyPathTransaction.objectStore(store)
      .index(indexName).keyPath
    const indexKeyPath = Array.isArray(rawKeyPath)
      ? [...rawKeyPath]
      : rawKeyPath
    await ensureLedgerSafePostgresPrimaryIndexCached({
      databaseName: PRIMARY_DB_NAME,
      storeName: String(store),
      indexName,
      indexKeyPath,
      indexValue: value,
    })
    const transaction = db.transaction([store], 'readonly')
    const index = transaction.objectStore(store).index(indexName)

    const records = await new Promise<DBSchema[T][]>((resolve, reject) => {
      const request = index.getAll(value)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    if (store === 'witnesses') {
      return this.hydrateWitnessRecords(records as DBSchema['witnesses'][]) as Promise<DBSchema[T][]>
    }
    return records
  }

  async getAllByCompoundIndex<T extends keyof DBSchema>(
    store: T,
    indexName: string,
    value: (string | boolean)[]
  ): Promise<DBSchema[T][]> {
    await this.ready()
    const db = this.ensureDB()
    const keyPathTransaction = db.transaction([store], 'readonly')
    const rawKeyPath = keyPathTransaction.objectStore(store)
      .index(indexName).keyPath
    const indexKeyPath = Array.isArray(rawKeyPath)
      ? [...rawKeyPath]
      : rawKeyPath
    await ensureLedgerSafePostgresPrimaryIndexCached({
      databaseName: PRIMARY_DB_NAME,
      storeName: String(store),
      indexName,
      indexKeyPath,
      indexValue: value,
    })
    const transaction = db.transaction([store], 'readonly')
    const index = transaction.objectStore(store).index(indexName)

    const records = await new Promise<DBSchema[T][]>((resolve, reject) => {
      const results: DBSchema[T][] = []
      const request = index.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor) {
          const key = cursor.key
          // Compound callers may provide either a complete key or a prefix
          // (for example [bookId, chapterId] for a three-part witness index).
          if (Array.isArray(key) && Array.isArray(value)) {
            if (key.length >= value.length && value.every((expected, i) => key[i] === expected)) {
              results.push(cursor.value)
            }
          } else if (key === value) {
            results.push(cursor.value)
          }
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
    if (store === 'witnesses') {
      return this.hydrateWitnessRecords(records as DBSchema['witnesses'][]) as Promise<DBSchema[T][]>
    }
    return records
  }

  async getAllByBooleanIndex<T extends keyof DBSchema>(
    store: T,
    indexName: string,
    value: boolean
  ): Promise<DBSchema[T][]> {
    await this.ready()
    await ensureLedgerSafePostgresPrimaryStoreCached({
      databaseName: PRIMARY_DB_NAME,
      storeName: String(store),
    })
    const db = this.ensureDB()
    const transaction = db.transaction([store], 'readonly')
    const objectStore = transaction.objectStore(store)
    const index = objectStore.index(indexName)

    return new Promise((resolve, reject) => {
      const results: DBSchema[T][] = []
      const request = index.openCursor()

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor) {
          if (cursor.key === value) {
            results.push(cursor.value)
          }
          cursor.continue()
        } else {
          resolve(results)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * Commit an exact set of record puts and deletes as one logical change.
   * PostgreSQL receives the complete mutation list before one IndexedDB
   * transaction updates the browser cache. Callers editing witness payloads
   * should continue to use put/putManyAcrossStores so the split witness_text
   * and witness_segmentation records are derived automatically.
   */
  async commitRecordChanges({
    puts = {},
    deletes = {},
  }: IndexedDBRecordChanges): Promise<void> {
    await this.ready()
    const putStoreNames = (Object.keys(puts) as Array<keyof DBSchema>)
      .filter(store => (puts[store]?.length || 0) > 0)
    const deleteStoreNames = (Object.keys(deletes) as Array<keyof DBSchema>)
      .filter(store => (deletes[store]?.length || 0) > 0)
    const storeNames = Array.from(new Set<keyof DBSchema>([
      ...putStoreNames,
      ...deleteStoreNames,
    ]))
    if (storeNames.length === 0) {
      return
    }

    const mutations: LedgerSafePrimaryRawMutation[] = []
    putStoreNames.forEach(store => {
      for (const record of puts[store] || []) {
        mutations.push({
          action: 'put',
          databaseName: PRIMARY_DB_NAME,
          storeName: String(store),
          key: indexedDbKeyFromRecord(
            record,
            PRIMARY_DB_STORE_KEY_PATHS[store],
          ),
          value: record,
        })
      }
    })
    deleteStoreNames.forEach(store => {
      for (const key of deletes[store] || []) {
        mutations.push({
          action: 'delete',
          databaseName: PRIMARY_DB_NAME,
          storeName: String(store),
          key,
        })
      }
    })

    return runLedgerSafePostgresPrimaryWrite({
      mutations,
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.ensureDB().transaction(
          storeNames,
          'readwrite',
        )
        transaction.oncomplete = () => {
          putStoreNames.forEach(store => {
            const records = puts[store] || []
            emitIndexedDBWriteObservation(
              store,
              records as DBSchema[keyof DBSchema][],
            )
          })
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('IndexedDB record change failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('IndexedDB record change aborted'),
        )
        putStoreNames.forEach(store => {
          const objectStore = transaction.objectStore(store)
          for (const record of puts[store] || []) {
            objectStore.put(record)
          }
        })
        deleteStoreNames.forEach(store => {
          const objectStore = transaction.objectStore(store)
          for (const key of deletes[store] || []) {
            objectStore.delete(key)
          }
        })
      }),
    })
  }

  async put<T extends keyof DBSchema>(store: T, data: DBSchema[T]): Promise<void> {
    if (store === 'witnesses') {
      await this.putManyAcrossStores({
        witnesses: [data as DBSchema['witnesses']],
      })
      return
    }
    return this.commitRecordChanges({
      puts: { [store]: [data] } as IndexedDBRecordWrites,
    })
  }

  async delete<T extends keyof DBSchema>(store: T, id: string): Promise<void> {
    return this.commitRecordChanges({
      deletes: { [store]: [id] } as IndexedDBRecordDeletes,
    })
  }

  async clear<T extends keyof DBSchema>(store: T): Promise<void> {
    await this.ready()
    return runLedgerSafePostgresPrimaryWrite({
      mutations: async () => {
        const transaction = this.ensureDB().transaction([store], 'readonly')
        const request = transaction.objectStore(store).getAllKeys()
        const keys = await new Promise<IDBValidKey[]>((resolve, reject) => {
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        })
        return keys.map(key => ({
          action: 'delete' as const,
          databaseName: PRIMARY_DB_NAME,
          storeName: String(store),
          key,
        }))
      },
      applyCache: () => new Promise<void>((resolve, reject) => {
        const transaction = this.ensureDB().transaction([store], 'readwrite')
        const objectStore = transaction.objectStore(store)
        objectStore.clear()
        transaction.oncomplete = () => resolve()
        transaction.onerror = () => reject(
          transaction.error || new Error('IndexedDB clear failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('IndexedDB clear aborted'),
        )
      }),
    })
  }

  /**
   * Delete every record in a store that satisfies the predicate, regardless of
   * the store's keyPath (cursor.delete() needs no key knowledge). Returns the
   * number of records removed.
   */
  async deleteMatching<T extends keyof DBSchema>(
    store: T,
    predicate: (record: DBSchema[T]) => boolean
  ): Promise<number> {
    await this.ready()
    return runLedgerSafePostgresPrimaryWrite({
      mutations: async () => {
        const records = await this.getAll(store)
        return records
          .filter(predicate)
          .map(record => ({
            action: 'delete' as const,
            databaseName: PRIMARY_DB_NAME,
            storeName: String(store),
            key: indexedDbKeyFromRecord(
              record,
              PRIMARY_DB_STORE_KEY_PATHS[store],
            ),
          }))
      },
      applyCache: () => {
        const transaction = this.ensureDB().transaction([store], 'readwrite')
        const objectStore = transaction.objectStore(store)
        return new Promise((resolve, reject) => {
          let removed = 0
          const request = objectStore.openCursor()
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null
            if (cursor) {
              if (predicate(cursor.value)) {
                cursor.delete()
                removed += 1
              }
              cursor.continue()
            } else {
              resolve(removed)
            }
          }
          request.onerror = () => reject(request.error)
        })
      },
    })
  }

  // ============================================================================
  // SECURITY ENHANCEMENT: Ownership Validation & Integrity Monitoring
  // ============================================================================

  /**
   * Validates witness ownership to prevent cross-chapter data leakage
   * CRITICAL: This prevents academic data contamination between different works
   */
  async validateWitnessOwnership(witnessId: string, expectedContext: OwnershipContext): Promise<SecurityValidationResult> {
    const violations: string[] = []

    try {
      const witness = await this.get('witnesses', witnessId)
      if (!witness) {
        violations.push(`Witness ${witnessId} not found`)
        return { isValid: false, violations, context: expectedContext }
      }

      // Validate book ownership
      if (witness.book_id !== expectedContext.book_id) {
        violations.push(`Witness belongs to book ${witness.book_id}, expected ${expectedContext.book_id}`)
      }

      // Validate chapter ownership if specified
      if (expectedContext.chapter_id && witness.chapter_id !== expectedContext.chapter_id) {
        violations.push(`Witness belongs to chapter ${witness.chapter_id}, expected ${expectedContext.chapter_id}`)
      }

      // Validate security checksum if present
      if (witness.security_checksum) {
        const expectedChecksum = await this.generateOwnershipChecksum(witness.book_id, witness.chapter_id, witnessId)
        if (witness.security_checksum !== expectedChecksum) {
          violations.push(`Security checksum mismatch for witness ${witnessId}`)
        }
      }

      return {
        isValid: violations.length === 0,
        violations,
        context: { book_id: witness.book_id, chapter_id: witness.chapter_id, witness_id: witnessId }
      }
    } catch (error) {
      violations.push(`Validation error: ${error}`)
      return { isValid: false, violations, context: expectedContext }
    }
  }

  /**
   * Validates baseline ownership with enhanced scoping
   * CRITICAL: Prevents baselines from being accessed by wrong witnesses
   */
  async validateBaselineOwnership(baselineHash: string, expectedContext: OwnershipContext): Promise<SecurityValidationResult> {
    const violations: string[] = []

    try {
      const baseline = await this.getBaselineByHash(baselineHash)
      if (!baseline) {
        violations.push(`Baseline ${baselineHash} not found`)
        return { isValid: false, violations, context: expectedContext }
      }

      // Validate complete ownership chain
      if (baseline.book_id !== expectedContext.book_id) {
        violations.push(`Baseline belongs to book ${baseline.book_id}, expected ${expectedContext.book_id}`)
      }

      if (expectedContext.chapter_id && baseline.chapter_id !== expectedContext.chapter_id) {
        violations.push(`Baseline belongs to chapter ${baseline.chapter_id}, expected ${expectedContext.chapter_id}`)
      }

      if (expectedContext.witness_id && baseline.witness_id !== expectedContext.witness_id) {
        violations.push(`Baseline belongs to witness ${baseline.witness_id}, expected ${expectedContext.witness_id}`)
      }

      // Validate data integrity
      if (baseline.data_integrity_hash) {
        const currentHash = await this.generateDataIntegrityHash(baseline.segmentation_result)
        if (baseline.data_integrity_hash !== currentHash) {
          violations.push(`Data integrity compromised for baseline ${baselineHash}`)
        }
      }

      return {
        isValid: violations.length === 0,
        violations,
        context: { book_id: baseline.book_id, chapter_id: baseline.chapter_id, witness_id: baseline.witness_id }
      }
    } catch (error) {
      violations.push(`Baseline validation error: ${error}`)
      return { isValid: false, violations, context: expectedContext }
    }
  }

  /**
   * Comprehensive integrity check for cross-witness contamination detection
   * CRITICAL: Detects if academic data has been corrupted across different works
   */
  async performIntegrityCheck(context?: OwnershipContext): Promise<IntegrityCheckResult> {
    const corrupted_items: string[] = []
    let checksum_mismatches = 0

    try {
      // Check witnesses integrity
      const witnessQuery = context?.book_id
        ? await this.getAllByIndex('witnesses', 'book_id', context.book_id)
        : await this.getAll('witnesses')

      for (const witness of witnessQuery) {
        if (witness.data_integrity_hash) {
          const expectedHash = await this.generateDataIntegrityHash({
            preprocessing_result: witness.preprocessing_result,
            segmentation_result: witness.segmentation_result
          })
          if (witness.data_integrity_hash !== expectedHash) {
            corrupted_items.push(`witness:${witness.id}`)
            checksum_mismatches++
          }
        }

        // Validate ownership chain
        if (witness.book_id && witness.chapter_id) {
          const chapter = await this.get('chapters', witness.chapter_id)
          if (!chapter || chapter.book_id !== witness.book_id) {
            corrupted_items.push(`witness_ownership:${witness.id}`)
          }
        }
      }

      // Check baselines integrity
      const baselineQuery = context?.book_id
        ? await this.getAllByIndex('segmentation_baselines', 'book_id', context.book_id)
        : await this.getAll('segmentation_baselines')

      for (const baseline of baselineQuery) {
        if (baseline.data_integrity_hash) {
          const expectedHash = await this.generateDataIntegrityHash(baseline.segmentation_result)
          if (baseline.data_integrity_hash !== expectedHash) {
            corrupted_items.push(`baseline:${baseline.id}`)
            checksum_mismatches++
          }
        }

        // Validate baseline ownership
        const witnessValidation = await this.validateWitnessOwnership(baseline.witness_id, {
          book_id: baseline.book_id,
          chapter_id: baseline.chapter_id,
          witness_id: baseline.witness_id
        })
        if (!witnessValidation.isValid) {
          corrupted_items.push(`baseline_ownership:${baseline.id}`)
        }
      }

      return {
        isIntact: corrupted_items.length === 0,
        corrupted_items,
        checksum_mismatches
      }
    } catch (error) {
      console.error('Integrity check failed:', error)
      return {
        isIntact: false,
        corrupted_items: ['integrity_check_failed'],
        checksum_mismatches: 0
      }
    }
  }

  /**
   * Generate ownership checksum for security validation
   */
  private async generateOwnershipChecksum(bookId: string, chapterId: string, itemId: string): Promise<string> {
    const content = `${bookId}:${chapterId}:${itemId}:${Date.now()}`
    const encoder = new TextEncoder()
    const data = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Generate data integrity hash for contamination detection
   */
  private async generateDataIntegrityHash(data: any): Promise<string> {
    const content = JSON.stringify(data, Object.keys(data).sort())
    const encoder = new TextEncoder()
    const encodedData = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-256', encodedData)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Generate access control hash for baseline security
   * FINAL SECURITY HARDENING: Additional security layer for access control
   */
  private async generateAccessControlHash(bookId: string, chapterId: string, itemId: string): Promise<string> {
    const timestamp = Math.floor(Date.now() / 86400000) // Daily rotation
    const content = `ACCESS_CONTROL:${bookId}:${chapterId}:${itemId}:${timestamp}:segmentation_agent_v2_security`
    const encoder = new TextEncoder()
    const data = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  // ============================================================================
  // SECURE WORKFLOW OPERATIONS WITH OWNERSHIP VALIDATION
  // ============================================================================

  /**
   * SECURITY ENHANCED: Get book chapters with ownership validation
   */
  async getBookChapters(bookId: string): Promise<DBSchema['chapters'][]> {
    // Validate book exists first
    const book = await this.get('books', bookId)
    if (!book) {
      throw new Error(`Book ${bookId} not found`)
    }

    const chapters = await this.getAllByIndex('chapters', 'book_id', bookId)

    // Security validation: Ensure all chapters belong to the correct book
    if (this.securityEnabled) {
      for (const chapter of chapters) {
        if (chapter.book_id !== bookId) {
          console.error(`SECURITY VIOLATION: Chapter ${chapter.id} belongs to book ${chapter.book_id}, not ${bookId}`)
          throw new Error(`Security violation: Chapter ownership mismatch`)
        }
      }
    }

    return chapters.sort((a, b) => a.order_index - b.order_index)
  }

  /**
   * SECURITY ENHANCED: Get chapter witnesses with ownership validation
   */
  async getChapterWitnesses(chapterId: string): Promise<DBSchema['witnesses'][]> {
    // Validate chapter exists and get its book context
    const chapter = await this.get('chapters', chapterId)
    if (!chapter) {
      throw new Error(`Chapter ${chapterId} not found`)
    }

    // Use compound index for secure query
    const witnesses = await this.getAllByCompoundIndex('witnesses', 'book_chapter_witness', [chapter.book_id, chapterId])

    // Security validation: Double-check witness ownership
    if (this.securityEnabled) {
      for (const witness of witnesses) {
        const validation = await this.validateWitnessOwnership(witness.id, {
          book_id: chapter.book_id,
          chapter_id: chapterId,
          witness_id: witness.id
        })
        if (!validation.isValid) {
          console.error(`SECURITY VIOLATION: Witness ${witness.id} failed ownership validation:`, validation.violations)
          throw new Error(`Security violation: Witness ownership validation failed`)
        }
      }
    }

    return witnesses.sort((a, b) => a.created_at - b.created_at)
  }

  /**
   * SECURITY ENHANCED: Get chapter alignments with ownership validation
   */
  async getChapterAlignments(chapterId: string): Promise<DBSchema['alignments'][]> {
    // Validate chapter exists and get its book context
    const chapter = await this.get('chapters', chapterId)
    if (!chapter) {
      throw new Error(`Chapter ${chapterId} not found`)
    }

    // Use compound index for secure query
    const alignments = await this.getAllByCompoundIndex('alignments', 'book_chapter', [chapter.book_id, chapterId])

    // Security validation: Ensure alignments belong to correct book/chapter
    if (this.securityEnabled) {
      for (const alignment of alignments) {
        if (alignment.book_id !== chapter.book_id || alignment.chapter_id !== chapterId) {
          console.error(`SECURITY VIOLATION: Alignment ${alignment.id} ownership mismatch`)
          throw new Error(`Security violation: Alignment ownership mismatch`)
        }
      }
    }

    return alignments.sort((a, b) => b.created_at - a.created_at)
  }

  // Batch operations for better performance
  async putMany<T extends keyof DBSchema>(store: T, items: DBSchema[T][]): Promise<void> {
    if (store === 'witnesses') {
      await this.putManyAcrossStores({
        witnesses: items as DBSchema['witnesses'][],
      })
      return
    }
    return this.commitRecordChanges({
      puts: { [store]: items } as IndexedDBRecordWrites,
    })
  }

  private async putManyAcrossStoresWithPrimary(
    updates: Partial<{ [K in keyof DBSchema]: DBSchema[K][] }>,
  ): Promise<void> {
    const preparedWrites = new Map<
      keyof DBSchema,
      DBSchema[keyof DBSchema][]
    >()
    const preparedDeletes = new Map<keyof DBSchema, IDBValidKey[]>()
    const queueWrite = (
      store: keyof DBSchema,
      record: DBSchema[keyof DBSchema],
    ): void => {
      const records = preparedWrites.get(store) || []
      records.push(record)
      preparedWrites.set(store, records)
    }
    const queueDelete = (store: keyof DBSchema, key: IDBValidKey): void => {
      const keys = preparedDeletes.get(store) || []
      keys.push(key)
      preparedDeletes.set(store, keys)
    }

    const prepareMutations = async (): Promise<
      LedgerSafePrimaryRawMutation[]
    > => {
      preparedWrites.clear()
      preparedDeletes.clear()
      for (const store of Object.keys(updates) as Array<keyof DBSchema>) {
        if (store === 'witnesses') {
          for (const witnessUpdate of updates.witnesses || []) {
            const current = await this.get('witnesses', witnessUpdate.id)
            const witness = {
              ...(current || {}),
              ...witnessUpdate,
            } as DBSchema['witnesses']
            queueWrite('witnesses', witness)
            if (hasWitnessTextPayloadFields(witnessUpdate)) {
              const textPayload = buildWitnessTextPayloadRecord(witness)
              if (textPayload) queueWrite('witness_text', textPayload)
              else queueDelete('witness_text', witness.id)
            }
            if (hasWitnessSegmentationPayloadFields(witnessUpdate)) {
              const segmentationPayload = buildWitnessSegmentationPayloadRecord(
                witness,
              )
              if (segmentationPayload) {
                queueWrite('witness_segmentation', segmentationPayload)
              } else {
                queueDelete('witness_segmentation', witness.id)
              }
            }
          }
          continue
        }
        for (const item of updates[store] || []) {
          queueWrite(store, item)
        }
      }

      const mutations: LedgerSafePrimaryRawMutation[] = []
      preparedWrites.forEach((records, store) => {
        records.forEach(record => mutations.push({
          action: 'put',
          databaseName: PRIMARY_DB_NAME,
          storeName: String(store),
          key: indexedDbKeyFromRecord(
            record,
            PRIMARY_DB_STORE_KEY_PATHS[store],
          ),
          value: record,
        }))
      })
      preparedDeletes.forEach((keys, store) => {
        keys.forEach(key => mutations.push({
          action: 'delete',
          databaseName: PRIMARY_DB_NAME,
          storeName: String(store),
          key,
        }))
      })
      return mutations
    }

    await runLedgerSafePostgresPrimaryWrite({
      mutations: prepareMutations,
      applyCache: () => new Promise<void>((resolve, reject) => {
        const storeNames = Array.from(new Set<keyof DBSchema>([
          ...Array.from(preparedWrites.keys()),
          ...Array.from(preparedDeletes.keys()),
        ]))
        if (storeNames.length === 0) {
          resolve()
          return
        }
        const transaction = this.ensureDB().transaction(
          storeNames,
          'readwrite',
        )
        transaction.oncomplete = () => {
          preparedWrites.forEach(
            (records, store) => emitIndexedDBWriteObservation(store, records),
          )
          resolve()
        }
        transaction.onerror = () => reject(
          transaction.error || new Error('IndexedDB transaction failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('IndexedDB transaction aborted'),
        )
        preparedWrites.forEach((records, store) => {
          const objectStore = transaction.objectStore(store)
          records.forEach(record => objectStore.put(record))
        })
        preparedDeletes.forEach((keys, store) => {
          const objectStore = transaction.objectStore(store)
          keys.forEach(key => objectStore.delete(key))
        })
      }),
    })
  }

  async putManyAcrossStores(updates: Partial<{ [K in keyof DBSchema]: DBSchema[K][] }>): Promise<void> {
    await this.ready()
    if ((updates.witnesses?.length || 0) === 0) {
      await this.commitRecordChanges({ puts: updates })
      return
    }
    if (isLedgerSafePostgresPrimaryCutoverRequired()) {
      await this.putManyAcrossStoresWithPrimary(updates)
      return
    }
    const db = this.ensureDB()
    const requestedStoreNames = (Object.keys(updates) as Array<keyof DBSchema>)
      .filter(store => (updates[store]?.length || 0) > 0)
    const storeNames = Array.from(new Set<keyof DBSchema>([
      ...requestedStoreNames,
      ...((updates.witnesses?.length || 0) > 0
        ? ['witness_text', 'witness_segmentation'] as Array<keyof DBSchema>
        : []),
    ]))

    if (storeNames.length === 0) {
      return
    }

    const transaction = db.transaction(storeNames, 'readwrite')
    const observedWrites = new Map<keyof DBSchema, DBSchema[keyof DBSchema][]>()
    const observeWrite = (store: keyof DBSchema, record: DBSchema[keyof DBSchema]): void => {
      const records = observedWrites.get(store) || []
      records.push(record)
      observedWrites.set(store, records)
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        observedWrites.forEach((records, store) => emitIndexedDBWriteObservation(store, records))
        resolve()
      }
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'))
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'))

      for (const store of requestedStoreNames) {
        if (store === 'witnesses') {
          const witnessStore = transaction.objectStore('witnesses')
          const textStore = transaction.objectStore('witness_text')
          const segmentationStore = transaction.objectStore('witness_segmentation')
          for (const witnessUpdate of updates.witnesses || []) {
            const readExisting = witnessStore.get(witnessUpdate.id)
            readExisting.onsuccess = () => {
              const witness = {
                ...(readExisting.result || {}),
                ...witnessUpdate,
              } as DBSchema['witnesses']
              witnessStore.put(witness)
              observeWrite('witnesses', witness)
              const textPayload = hasWitnessTextPayloadFields(witnessUpdate)
                ? buildWitnessTextPayloadRecord(witness)
                : null
              const segmentationPayload = hasWitnessSegmentationPayloadFields(witnessUpdate)
                ? buildWitnessSegmentationPayloadRecord(witness)
                : null
              if (textPayload) {
                textStore.put(textPayload)
                observeWrite('witness_text', textPayload)
              } else if (hasWitnessTextPayloadFields(witnessUpdate)) {
                textStore.delete(witness.id)
              }
              if (segmentationPayload) {
                segmentationStore.put(segmentationPayload)
                observeWrite('witness_segmentation', segmentationPayload)
              } else if (hasWitnessSegmentationPayloadFields(witnessUpdate)) {
                segmentationStore.delete(witness.id)
              }
            }
          }
          continue
        }
        const objectStore = transaction.objectStore(store)
        for (const item of updates[store] || []) {
          objectStore.put(item)
          observeWrite(store, item)
        }
      }
    })
  }

  async compareAndSetLedgerSafeAuthorityHead({
    expected,
    next,
  }: {
    expected?: Pick<LedgerSafeAuthorityHeadRecord, 'chapterId' | 'authorityRevisionId' | 'contentHash'>
    next: LedgerSafeAuthorityHeadRecord
  }): Promise<LedgerSafeAuthorityHeadRecord> {
    await this.ready()
    const db = this.ensureDB()
    const matchesExpectedAuthority = (
      current: LedgerSafeAuthorityHeadRecord | undefined,
    ): boolean => (
      expected
        ? Boolean(current)
          && current?.chapterId === expected.chapterId
          && current.authorityRevisionId === expected.authorityRevisionId
          && current.contentHash === expected.contentHash
        : !current
    )
    const readCurrent = (): Promise<LedgerSafeAuthorityHeadRecord | undefined> =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(
          ['ledger_safe_authority_heads'],
          'readonly',
        )
        const request = transaction
          .objectStore('ledger_safe_authority_heads')
          .get(next.chapterId)
        request.onsuccess = () => resolve(
          request.result as LedgerSafeAuthorityHeadRecord | undefined,
        )
        request.onerror = () => reject(
          request.error || new Error('Authority read failed'),
        )
      })
    const applyLocalCompareAndSet = (
      rejectOnConflict: boolean,
    ): Promise<LedgerSafeAuthorityHeadRecord> =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(
          ['ledger_safe_authority_heads'],
          'readwrite',
        )
        const store = transaction.objectStore('ledger_safe_authority_heads')
        let result = next
        const request = store.get(next.chapterId)
        request.onsuccess = () => {
          const current = request.result as
            | LedgerSafeAuthorityHeadRecord
            | undefined
          if (!matchesExpectedAuthority(current)) {
            if (rejectOnConflict) {
              reject(new LedgerSafeAgentAtomicConflictError(
                'The authoritative chapter revision changed after PostgreSQL committed.',
              ))
              try {
                transaction.abort()
              } catch {
                // Preserve the explicit post-commit conflict.
              }
              return
            }
            if (current) result = current
            return
          }
          store.put(next)
        }
        transaction.oncomplete = () => resolve(result)
        transaction.onerror = () => reject(
          transaction.error || new Error('Authority synchronization failed'),
        )
        transaction.onabort = () => reject(
          transaction.error || new Error('Authority synchronization aborted'),
        )
      })

    if (!isLedgerSafePostgresPrimaryCutoverRequired()) {
      return applyLocalCompareAndSet(false)
    }

    let shouldWrite = false
    let result = next
    return runLedgerSafePostgresPrimaryWrite({
      mutations: async () => {
        const current = await readCurrent()
        const matchesExpected = expected
          ? matchesExpectedAuthority(current)
          : !current
        if (!matchesExpected) {
          if (current) result = current
          return []
        }
        shouldWrite = true
        return [{
          action: 'put',
          databaseName: PRIMARY_DB_NAME,
          storeName: 'ledger_safe_authority_heads',
          key: next.chapterId,
          value: next,
        }]
      },
      applyCache: async () => {
        if (!shouldWrite) return result
        return applyLocalCompareAndSet(true)
      },
    })
  }

  async commitLedgerSafeAgentMutation(mutation: LedgerSafeAgentAtomicMutation): Promise<void> {
    await this.ready()
    const db = this.ensureDB()
    const storeNames: Array<keyof DBSchema> = [
      'anchored_rows',
      'witnesses',
      'witness_text',
      'witness_segmentation',
      'chapter_character_layers',
      'ledger_safe_edit_events',
      'ledger_safe_agent_change_sets',
      'ledger_safe_authority_heads',
    ]
    const createGuards = (transaction: IDBTransaction): Array<{
      request: IDBRequest
      validate: (value: any) => boolean
      message: string
    }> => {
      const guards: Array<{
        request: IDBRequest
        validate: (value: any) => boolean
        message: string
      }> = []

      guards.push({
        request: transaction.objectStore('ledger_safe_authority_heads').get(mutation.expectedAuthority.chapterId),
        validate: value => Boolean(value)
          && value.authorityRevisionId === mutation.expectedAuthority.authorityRevisionId
          && value.contentHash === mutation.expectedAuthority.contentHash,
        message: 'The authoritative chapter revision is stale.',
      })
      guards.push({
        request: transaction.objectStore('ledger_safe_agent_change_sets').get(mutation.changeSetId),
        validate: value => Boolean(value) && value.status === mutation.expectedChangeSetStatus,
        message: 'The agent change set is no longer in the expected review state.',
      })
      mutation.expectedState.rows.forEach(row => {
        const expected = serializeLedgerSafeAtomicRow(row)
        guards.push({
          request: transaction.objectStore('anchored_rows').get(row.id),
          validate: value => ledgerSafeAtomicValuesMatch(value, expected),
          message: `Collation row ${row.id} changed while the agent sequence was under review.`,
        })
      })
      mutation.expectedState.witnesses.forEach(witness => {
        guards.push({
          request: transaction.objectStore('witnesses').get(witness.id),
          validate: value => ledgerSafeAtomicValuesMatch(value, witness),
          message: `Witness ${witness.id} changed while the agent sequence was under review.`,
        })
      })
      if (mutation.expectedState.characterLayer) {
        guards.push({
          request: transaction.objectStore('chapter_character_layers').get(mutation.expectedState.characterLayer.chapter_id),
          validate: value => ledgerSafeAtomicValuesMatch(value, mutation.expectedState.characterLayer),
          message: 'The Character Index changed while the agent sequence was under review.',
        })
      }
      return guards
    }

    const validateCurrentState = (): Promise<void> =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeNames, 'readonly')
        const guards = createGuards(transaction)
        let conflict: Error | null = null
        guards.forEach(guard => {
          guard.request.onsuccess = () => {
            if (!guard.validate(guard.request.result) && !conflict) {
              conflict = new LedgerSafeAgentAtomicConflictError(guard.message)
              try {
                transaction.abort()
              } catch {
                // The transaction may already be completing.
              }
            }
          }
        })
        transaction.oncomplete = () => {
          if (conflict) reject(conflict)
          else resolve()
        }
        transaction.onerror = () => {
          // onabort supplies the final rejection.
        }
        transaction.onabort = () => reject(
          conflict
          || transaction.error
          || new Error('Ledger Safe agent validation aborted'),
        )
      })

    const applyLocalMutation = (): Promise<void> =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeNames, 'readwrite')
        let conflict: Error | null = null
        let writesQueued = false
        const guards = createGuards(transaction)

        const abortWithConflict = (message: string) => {
          if (!conflict) {
            conflict = new LedgerSafeAgentAtomicConflictError(message)
          }
          try {
            transaction.abort()
          } catch {
            // The transaction may already be aborting because another guard failed.
          }
        }

        let completedGuards = 0
        const queueWrites = () => {
          if (writesQueued || conflict || completedGuards !== guards.length) {
            return
          }
          try {
            writesQueued = true
            const rowStore = transaction.objectStore('anchored_rows')
            mutation.nextState.rows.forEach(row => rowStore.put(serializeLedgerSafeAtomicRow(row)))
            const witnessStore = transaction.objectStore('witnesses')
            const witnessTextStore = transaction.objectStore('witness_text')
            const witnessSegmentationStore = transaction.objectStore(
              'witness_segmentation',
            )
            mutation.nextState.witnesses.forEach(witness => {
              witnessStore.put(witness)
              if (hasWitnessTextPayloadFields(witness)) {
                const textPayload = buildWitnessTextPayloadRecord(witness)
                if (textPayload) witnessTextStore.put(textPayload)
                else witnessTextStore.delete(witness.id)
              }
              if (hasWitnessSegmentationPayloadFields(witness)) {
                const segmentationPayload =
                  buildWitnessSegmentationPayloadRecord(witness)
                if (segmentationPayload) {
                  witnessSegmentationStore.put(segmentationPayload)
                } else {
                  witnessSegmentationStore.delete(witness.id)
                }
              }
            })
            if (mutation.nextState.characterLayer) {
              transaction.objectStore('chapter_character_layers').put(mutation.nextState.characterLayer)
            }
            transaction.objectStore('ledger_safe_edit_events').put(mutation.editorialEvent)
            transaction.objectStore('ledger_safe_agent_change_sets').put(mutation.nextChangeSet)
            transaction.objectStore('ledger_safe_authority_heads').put(mutation.nextAuthority)
          } catch (error) {
            conflict = error instanceof Error ? error : new Error(String(error))
            try {
              transaction.abort()
            } catch {
              // Preserve the original write error.
            }
          }
        }

        guards.forEach(guard => {
          guard.request.onsuccess = () => {
            if (!guard.validate(guard.request.result)) {
              abortWithConflict(guard.message)
              return
            }
            completedGuards += 1
            queueWrites()
          }
        })

        transaction.oncomplete = () => resolve()
        transaction.onerror = () => {
          // onabort supplies the final rejection and preserves an explicit conflict.
        }
        transaction.onabort = () => reject(
          conflict
          || transaction.error
          || new Error('Ledger Safe agent transaction aborted'),
        )
      })

    if (!isLedgerSafePostgresPrimaryCutoverRequired()) {
      return applyLocalMutation()
    }

    return runLedgerSafePostgresPrimaryWrite({
      mutations: async () => {
        await validateCurrentState()
        const rawMutations: LedgerSafePrimaryRawMutation[] = [
          ...mutation.nextState.rows.map(row => ({
            action: 'put' as const,
            databaseName: PRIMARY_DB_NAME,
            storeName: 'anchored_rows',
            key: row.id,
            value: serializeLedgerSafeAtomicRow(row),
          })),
          ...mutation.nextState.witnesses.map(witness => ({
            action: 'put' as const,
            databaseName: PRIMARY_DB_NAME,
            storeName: 'witnesses',
            key: witness.id,
            value: witness,
          })),
        ]
        mutation.nextState.witnesses.forEach(witness => {
          if (hasWitnessTextPayloadFields(witness)) {
            const textPayload = buildWitnessTextPayloadRecord(witness)
            rawMutations.push(textPayload
              ? {
                  action: 'put',
                  databaseName: PRIMARY_DB_NAME,
                  storeName: 'witness_text',
                  key: witness.id,
                  value: textPayload,
                }
              : {
                  action: 'delete',
                  databaseName: PRIMARY_DB_NAME,
                  storeName: 'witness_text',
                  key: witness.id,
                })
          }
          if (hasWitnessSegmentationPayloadFields(witness)) {
            const segmentationPayload =
              buildWitnessSegmentationPayloadRecord(witness)
            rawMutations.push(segmentationPayload
              ? {
                  action: 'put',
                  databaseName: PRIMARY_DB_NAME,
                  storeName: 'witness_segmentation',
                  key: witness.id,
                  value: segmentationPayload,
                }
              : {
                  action: 'delete',
                  databaseName: PRIMARY_DB_NAME,
                  storeName: 'witness_segmentation',
                  key: witness.id,
                })
          }
        })
        if (mutation.nextState.characterLayer) {
          rawMutations.push({
            action: 'put',
            databaseName: PRIMARY_DB_NAME,
            storeName: 'chapter_character_layers',
            key: mutation.nextState.characterLayer.chapter_id,
            value: mutation.nextState.characterLayer,
          })
        }
        rawMutations.push(
          {
            action: 'put',
            databaseName: PRIMARY_DB_NAME,
            storeName: 'ledger_safe_edit_events',
            key: mutation.editorialEvent.id,
            value: mutation.editorialEvent,
          },
          {
            action: 'put',
            databaseName: PRIMARY_DB_NAME,
            storeName: 'ledger_safe_agent_change_sets',
            key: mutation.nextChangeSet.id,
            value: mutation.nextChangeSet,
          },
          {
            action: 'put',
            databaseName: PRIMARY_DB_NAME,
            storeName: 'ledger_safe_authority_heads',
            key: mutation.nextAuthority.chapterId,
            value: mutation.nextAuthority,
          },
        )
        return rawMutations
      },
      applyCache: applyLocalMutation,
    })
  }

  async exportAllStores(): Promise<Record<string, any[]>> {
    await this.ready()
    const db = this.ensureDB()
    const transaction = db.transaction(PRIMARY_DB_STORE_NAMES, 'readonly')

    return new Promise((resolve, reject) => {
      const snapshot: Record<string, any[]> = {}
      transaction.oncomplete = () => resolve(snapshot)
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB export transaction failed'))
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB export transaction aborted'))

      for (const store of PRIMARY_DB_STORE_NAMES) {
        const request = transaction.objectStore(store).getAll()
        request.onsuccess = () => {
          snapshot[store] = request.result
        }
      }
    })
  }

  async replaceAllStores(snapshot: Partial<Record<keyof DBSchema, DBSchema[keyof DBSchema][]>>): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Replacing the complete ScholarlyWorkflowDB cache',
    )
    await this.ready()
    const db = this.ensureDB()
    const transaction = db.transaction(PRIMARY_DB_STORE_NAMES, 'readwrite')

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error || new Error('IndexedDB replacement transaction failed'))
      transaction.onabort = () => reject(transaction.error || new Error('IndexedDB replacement transaction aborted'))

      try {
        for (const store of PRIMARY_DB_STORE_NAMES) {
          const objectStore = transaction.objectStore(store)
          objectStore.clear()

          const records = snapshot[store]
          if (!Array.isArray(records)) {
            continue
          }
          for (const record of records) {
            objectStore.put(record)
          }
        }
      } catch (error) {
        transaction.abort()
        reject(error)
      }
    })
  }

  // Storage usage estimation (approximate)
  async getStorageUsage(): Promise<{ used: number; estimatedPercentage: number }> {
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      try {
        const estimate = await navigator.storage.estimate()
        if (Number.isFinite(estimate.usage)) {
          const used = Math.max(0, estimate.usage || 0)
          const quota = Number.isFinite(estimate.quota) ? Math.max(0, estimate.quota || 0) : 0
          return {
            used,
            estimatedPercentage: quota > 0
              ? Math.min(100, Math.round((used / quota) * 100))
              : 0
          }
        }
      } catch {
        // Fall back to a store-level estimate for older or restricted browsers.
      }
    }

    const stores: (keyof DBSchema)[] = PRIMARY_DB_STORE_NAMES
    let totalSize = 0

    for (const store of stores) {
      const items = await this.getAll(store)
      totalSize += estimateSerializedRecordCollectionSize(items)
    }

    // IndexedDB typically allows much more (50MB+), but exact limits vary
    // We'll use 50MB as a conservative estimate
    const estimatedLimit = 50 * 1024 * 1024
    const percentage = Math.round((totalSize / estimatedLimit) * 100)

    return {
      used: totalSize,
      estimatedPercentage: Math.min(percentage, 100)
    }
  }

  // Clear all data
  async clearAll(): Promise<void> {
    assertLedgerSafePostgresPrimaryOperationSupported(
      'Clearing the complete ScholarlyWorkflowDB cache',
    )
    await this.ready()
    const db = this.ensureDB()
    const stores: (keyof DBSchema)[] = PRIMARY_DB_STORE_NAMES

    const transaction = db.transaction(stores, 'readwrite')

    return new Promise((resolve, reject) => {
      let completed = 0
      const total = stores.length

      stores.forEach((store) => {
        const objectStore = transaction.objectStore(store)
        const request = objectStore.clear()

        request.onsuccess = () => {
          completed++
          if (completed === total) resolve()
        }
        request.onerror = () => reject(request.error)
      })
    })
  }

  // ============================================================================
  // Hybrid Storage Methods for Enhanced Edit Management
  // ============================================================================

  // ============================================================================
  // SECURITY ENHANCED: Baseline Storage with Proper Scoping
  // ============================================================================

  /**
   * SECURITY ENHANCED: Store segmentation baseline with complete ownership context
   * FINAL SECURITY HARDENING: Enhanced with cryptographic protection
   */
  async storeSegmentationBaseline(
    witnessId: string,
    segmentationResult: any,
    metadata: { provider: string; model: string; processing_time: number; security_signature?: any; security_context?: any; baseline_hash?: string }
  ): Promise<string> {
    // SECURITY FIX: Get witness context for proper scoping
    const witness = await this.get('witnesses', witnessId)
    if (!witness) {
      throw new Error(`Witness ${witnessId} not found - cannot store baseline`)
    }

    // Validate witness ownership before storing baseline
    if (this.securityEnabled) {
      const validation = await this.validateWitnessOwnership(witnessId, {
        book_id: witness.book_id,
        chapter_id: witness.chapter_id,
        witness_id: witnessId
      })
      if (!validation.isValid) {
        throw new Error(`Cannot store baseline: Witness ownership validation failed: ${validation.violations.join(', ')}`)
      }
    }

    // Use provided secure hash if available, otherwise generate standard hash
    const baselineHash = metadata.baseline_hash || await this.generateContentHash(segmentationResult)
    const securityChecksum = await this.generateOwnershipChecksum(witness.book_id, witness.chapter_id, witnessId)
    const dataIntegrityHash = await this.generateDataIntegrityHash(segmentationResult)

    // Enhanced security metadata
    const enhancedMetadata = {
      ...metadata,
      total_units: this.countUnitsInResult(segmentationResult),
      created_at: Date.now(),
      security_level: metadata.security_context?.security_level || 'standard',
      cryptographic_signature: metadata.security_signature,
      access_control_hash: await this.generateAccessControlHash(witness.book_id, witness.chapter_id, witnessId)
    }

    const baseline: DBSchema['segmentation_baselines'] = {
      id: `baseline_${witnessId}_${Date.now()}`,
      witness_id: witnessId,
      chapter_id: witness.chapter_id,  // SECURITY FIX: Add chapter scoping
      book_id: witness.book_id,        // SECURITY FIX: Add book scoping
      baseline_hash: baselineHash,
      segmentation_result: segmentationResult,
      metadata: enhancedMetadata,
      is_current_baseline: true,
      security_checksum: securityChecksum,
      data_integrity_hash: dataIntegrityHash
    }

    // Mark previous baselines as non-current (with security validation)
    await this.markPreviousBaselinesAsCurrent(witnessId, false)

    await this.put('segmentation_baselines', baseline)
    console.log(`SECURITY: Stored baseline ${baselineHash} for witness ${witnessId} with full ownership context`)
    return baselineHash
  }

  /**
   * SECURITY ENHANCED: Get current baseline with ownership validation
   */
  async getCurrentBaseline(witnessId: string): Promise<DBSchema['segmentation_baselines'] | null> {
    // Get witness context for security validation
    const witness = await this.get('witnesses', witnessId)
    if (!witness) {
      throw new Error(`Witness ${witnessId} not found`)
    }

    const baselines = await this.getAllByCompoundIndex('segmentation_baselines', 'witness_current', [witnessId, true])
    if (baselines.length === 0) return null

    const baseline = baselines[0]

    // SECURITY VALIDATION: Ensure baseline belongs to correct witness/chapter/book
    if (this.securityEnabled) {
      const validation = await this.validateBaselineOwnership(baseline.baseline_hash, {
        book_id: witness.book_id,
        chapter_id: witness.chapter_id,
        witness_id: witnessId
      })
      if (!validation.isValid) {
        console.error(`SECURITY VIOLATION: Baseline ownership validation failed:`, validation.violations)
        throw new Error(`Security violation: Baseline access denied`)
      }
    }

    return baseline
  }

  /**
   * SECURITY ENHANCED: Get baseline by hash with integrity verification
   */
  async getBaselineByHash(baselineHash: string): Promise<DBSchema['segmentation_baselines'] | null> {
    const baselines = await this.getAllByIndex('segmentation_baselines', 'baseline_hash', baselineHash)
    if (baselines.length === 0) return null

    const baseline = baselines[0]

    // SECURITY VALIDATION: Verify data integrity
    if (this.securityEnabled && baseline.data_integrity_hash) {
      const currentHash = await this.generateDataIntegrityHash(baseline.segmentation_result)
      if (baseline.data_integrity_hash !== currentHash) {
        console.error(`SECURITY VIOLATION: Baseline ${baselineHash} data integrity compromised`)
        throw new Error(`Security violation: Baseline data integrity compromised`)
      }
    }

    return baseline
  }

  async markPreviousBaselinesAsCurrent(witnessId: string, isCurrent: boolean): Promise<void> {
    const baselines = await this.getAllByIndex('segmentation_baselines', 'witness_id', witnessId)
    const updates = baselines.map(baseline => ({
      ...baseline,
      is_current_baseline: isCurrent
    }))
    await this.putMany('segmentation_baselines', updates)
  }

  // ============================================================================
  // SECURITY ENHANCED: Edit Operations Storage with Full Context
  // ============================================================================

  /**
   * SECURITY ENHANCED: Store edit operation with complete ownership validation
   */
  async storeEditOperation(
    sessionId: string,
    baselineHash: string,
    operationType: 'merge' | 'split' | 'cross_chunk_merge',
    operationData: any,
    sequenceNumber: number
  ): Promise<string> {
    // SECURITY FIX: Get baseline context for ownership validation
    const baseline = await this.getBaselineByHash(baselineHash)
    if (!baseline) {
      throw new Error(`Baseline ${baselineHash} not found - cannot store edit operation`)
    }

    // Validate baseline ownership before storing operation
    if (this.securityEnabled) {
      const validation = await this.validateBaselineOwnership(baselineHash, {
        book_id: baseline.book_id,
        chapter_id: baseline.chapter_id,
        witness_id: baseline.witness_id
      })
      if (!validation.isValid) {
        throw new Error(`Cannot store edit operation: Baseline ownership validation failed: ${validation.violations.join(', ')}`)
      }
    }

    const securityChecksum = await this.generateOwnershipChecksum(baseline.book_id, baseline.chapter_id, baseline.witness_id)

    const operation: DBSchema['edit_operations'] = {
      id: `op_${sessionId}_${sequenceNumber}`,
      session_id: sessionId,
      witness_id: baseline.witness_id,  // SECURITY FIX: Add witness context
      chapter_id: baseline.chapter_id,  // SECURITY FIX: Add chapter context
      book_id: baseline.book_id,        // SECURITY FIX: Add book context
      baseline_hash: baselineHash,
      operation_type: operationType,
      operation_data: operationData,
      sequence_number: sequenceNumber,
      timestamp: Date.now(),
      applied_locally: true,
      synced_to_server: false,
      security_checksum: securityChecksum
    }

    await this.put('edit_operations', operation)
    console.log(`SECURITY: Stored edit operation ${operation.id} with full ownership context`)
    return operation.id
  }

  /**
   * SECURITY ENHANCED: Get edit operations by session with ownership validation
   */
  async getEditOperationsBySession(sessionId: string): Promise<DBSchema['edit_operations'][]> {
    const operations = await this.getAllByIndex('edit_operations', 'session_id', sessionId)

    // SECURITY VALIDATION: Ensure all operations in session belong to same witness/chapter/book
    if (this.securityEnabled && operations.length > 0) {
      const firstOp = operations[0]
      const expectedContext = {
        book_id: firstOp.book_id,
        chapter_id: firstOp.chapter_id,
        witness_id: firstOp.witness_id
      }

      for (const op of operations) {
        if (op.book_id !== expectedContext.book_id ||
          op.chapter_id !== expectedContext.chapter_id ||
          op.witness_id !== expectedContext.witness_id) {
          console.error(`SECURITY VIOLATION: Edit operation ${op.id} context mismatch in session ${sessionId}`)
          throw new Error(`Security violation: Edit operation context mismatch`)
        }
      }
    }

    return operations.sort((a, b) => a.sequence_number - b.sequence_number)
  }

  async getUnsyncedOperations(): Promise<DBSchema['edit_operations'][]> {
    const operations = await this.getAllByBooleanIndex('edit_operations', 'sync_status', false)
    return operations.sort((a, b) => a.timestamp - b.timestamp)
  }

  async markOperationAsSynced(operationId: string): Promise<void> {
    const operation = await this.get('edit_operations', operationId)
    if (operation) {
      operation.synced_to_server = true
      await this.put('edit_operations', operation)
    }
  }

  // ============================================================================
  // SECURITY ENHANCED: Processing Checkpoint Management with Full Context
  // ============================================================================

  /**
   * SECURITY ENHANCED: Store processing checkpoint with ownership context
   */
  async storeProcessingCheckpoint(
    witnessId: string,
    stage: 'preprocessing' | 'segmentation',
    progress: number,
    checkpointData: any
  ): Promise<string> {
    // SECURITY FIX: Get witness context for proper scoping
    const witness = await this.get('witnesses', witnessId)
    if (!witness) {
      throw new Error(`Witness ${witnessId} not found - cannot store checkpoint`)
    }

    // Validate witness ownership before storing checkpoint
    if (this.securityEnabled) {
      const validation = await this.validateWitnessOwnership(witnessId, {
        book_id: witness.book_id,
        chapter_id: witness.chapter_id,
        witness_id: witnessId
      })
      if (!validation.isValid) {
        throw new Error(`Cannot store checkpoint: Witness ownership validation failed: ${validation.violations.join(', ')}`)
      }
    }

    const securityChecksum = await this.generateOwnershipChecksum(witness.book_id, witness.chapter_id, witnessId)

    const checkpoint: DBSchema['processing_checkpoints'] = {
      id: `checkpoint_${witnessId}_${stage}_${Date.now()}`,
      witness_id: witnessId,
      chapter_id: witness.chapter_id,  // SECURITY FIX: Add chapter context
      book_id: witness.book_id,        // SECURITY FIX: Add book context
      stage: stage,
      progress: progress,
      checkpoint_data: checkpointData,
      created_at: Date.now(),
      can_resume: progress < 100,
      security_checksum: securityChecksum
    }

    await this.put('processing_checkpoints', checkpoint)
    console.log(`SECURITY: Stored checkpoint ${checkpoint.id} with full ownership context`)
    return checkpoint.id
  }

  /**
   * SECURITY ENHANCED: Get latest checkpoint with ownership validation
   */
  async getLatestCheckpoint(witnessId: string, stage: 'preprocessing' | 'segmentation'): Promise<DBSchema['processing_checkpoints'] | null> {
    // Get witness context for security validation
    const witness = await this.get('witnesses', witnessId)
    if (!witness) {
      throw new Error(`Witness ${witnessId} not found`)
    }

    const checkpoints = await this.getAllByCompoundIndex('processing_checkpoints', 'witness_stage', [witnessId, stage])
    if (checkpoints.length === 0) return null

    const latest = checkpoints.reduce((latest, current) =>
      current.created_at > latest.created_at ? current : latest
    )

    // SECURITY VALIDATION: Ensure checkpoint belongs to correct witness/chapter/book
    if (this.securityEnabled) {
      if (latest.book_id !== witness.book_id || latest.chapter_id !== witness.chapter_id) {
        console.error(`SECURITY VIOLATION: Checkpoint ${latest.id} ownership mismatch`)
        throw new Error(`Security violation: Checkpoint access denied`)
      }
    }

    return latest
  }

  async cleanupOldCheckpoints(witnessId: string, keepCount: number = 3): Promise<number> {
    const checkpoints = await this.getAllByIndex('processing_checkpoints', 'witness_id', witnessId)
    const sorted = checkpoints.sort((a, b) => b.created_at - a.created_at)
    const toDelete = sorted.slice(keepCount)

    let deleted = 0
    for (const checkpoint of toDelete) {
      await this.delete('processing_checkpoints', checkpoint.id)
      deleted++
    }

    return deleted
  }

  // Utility methods
  private async generateContentHash(content: any): Promise<string> {
    const contentStr = JSON.stringify(content)
    const encoder = new TextEncoder()
    const data = encoder.encode(contentStr)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private countUnitsInResult(segmentationResult: any): number {
    if (!segmentationResult?.chunk_results) return 0
    return segmentationResult.chunk_results.reduce((total: number, chunk: any) => {
      return total + (chunk.micro_units?.length || 0)
    }, 0)
  }

  // Enhanced storage usage with new stores
  async getEnhancedStorageUsage(): Promise<{
    used: number;
    estimatedPercentage: number;
    breakdown: Record<keyof DBSchema, number>
  }> {
    const stores: (keyof DBSchema)[] = [
      'books',
      'chapters',
      'witnesses',
      'alignments',
      'segmentation_baselines',
      'edit_operations',
      'processing_checkpoints',
      'chapter_device_layers',
      'witness_device_layers',
      'device_annotation_runs',
      'chapter_character_layers',
      'analytical_task_sets',
    ]

    const breakdown: Record<keyof DBSchema, number> = {} as any
    let totalSize = 0

    for (const store of stores) {
      const items = await this.getAll(store)
      const size = estimateSerializedRecordCollectionSize(items)
      breakdown[store] = size
      totalSize += size
    }

    const estimatedLimit = 50 * 1024 * 1024 // 50MB conservative estimate
    const percentage = Math.round((totalSize / estimatedLimit) * 100)

    return {
      used: totalSize,
      estimatedPercentage: Math.min(percentage, 100),
      breakdown
    }
  }

  // ============================================================================
  // SECURITY TESTING AND MONITORING
  // ============================================================================

  /**
   * Comprehensive security test suite for database isolation
   */
  async runSecurityTests(): Promise<{
    passed: number;
    failed: number;
    results: Array<{ test: string; passed: boolean; message: string }>
  }> {
    const results: Array<{ test: string; passed: boolean; message: string }> = []
    let passed = 0
    let failed = 0

    // Test 1: Compound index effectiveness
    try {
      const witnesses = await this.getAll('witnesses')
      if (witnesses.length > 0) {
        const witness = witnesses[0]
        const secureResults = await this.getAllByCompoundIndex('witnesses', 'book_chapter_witness',
          [witness.book_id, witness.chapter_id, witness.id])

        if (secureResults.length === 1 && secureResults[0].id === witness.id) {
          results.push({ test: 'Compound Index Isolation', passed: true, message: 'Compound indexing working correctly' })
          passed++
        } else {
          results.push({ test: 'Compound Index Isolation', passed: false, message: 'Compound indexing failed' })
          failed++
        }
      }
    } catch (error) {
      results.push({ test: 'Compound Index Isolation', passed: false, message: `Error: ${error}` })
      failed++
    }

    // Test 2: Ownership validation effectiveness
    try {
      const witnesses = await this.getAll('witnesses')
      if (witnesses.length > 0) {
        const witness = witnesses[0]
        const validation = await this.validateWitnessOwnership(witness.id, {
          book_id: witness.book_id,
          chapter_id: witness.chapter_id,
          witness_id: witness.id
        })

        if (validation.isValid) {
          results.push({ test: 'Ownership Validation', passed: true, message: 'Ownership validation working correctly' })
          passed++
        } else {
          results.push({ test: 'Ownership Validation', passed: false, message: `Validation failed: ${validation.violations.join(', ')}` })
          failed++
        }
      }
    } catch (error) {
      results.push({ test: 'Ownership Validation', passed: false, message: `Error: ${error}` })
      failed++
    }

    // Test 3: Baseline security isolation
    try {
      const baselines = await this.getAll('segmentation_baselines')
      if (baselines.length > 0) {
        const baseline = baselines[0]
        const validation = await this.validateBaselineOwnership(baseline.baseline_hash, {
          book_id: baseline.book_id,
          chapter_id: baseline.chapter_id,
          witness_id: baseline.witness_id
        })

        if (validation.isValid) {
          results.push({ test: 'Baseline Security', passed: true, message: 'Baseline isolation working correctly' })
          passed++
        } else {
          results.push({ test: 'Baseline Security', passed: false, message: `Baseline validation failed: ${validation.violations.join(', ')}` })
          failed++
        }
      }
    } catch (error) {
      results.push({ test: 'Baseline Security', passed: false, message: `Error: ${error}` })
      failed++
    }

    // Test 4: Integrity monitoring
    try {
      const integrityResult = await this.performIntegrityCheck()
      if (integrityResult.isIntact) {
        results.push({ test: 'Data Integrity', passed: true, message: 'All data integrity checks passed' })
        passed++
      } else {
        results.push({ test: 'Data Integrity', passed: false, message: `Integrity issues: ${integrityResult.corrupted_items.length} corrupted items` })
        failed++
      }
    } catch (error) {
      results.push({ test: 'Data Integrity', passed: false, message: `Error: ${error}` })
      failed++
    }

    return { passed, failed, results }
  }

  /**
   * Enable or disable security validation (for testing purposes)
   */
  setSecurityEnabled(enabled: boolean): void {
    this.securityEnabled = enabled
    console.log(`Security validation ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Get security status and configuration
   */
  getSecurityStatus(): {
    enabled: boolean;
    features: string[];
    last_integrity_check?: number;
  } {
    return {
      enabled: this.securityEnabled,
      features: [
        'Compound Indexing',
        'Ownership Validation',
        'Baseline Security',
        'Integrity Monitoring',
        'Cross-witness Isolation',
        'Data Contamination Detection'
      ]
    }
  }

  // Migration helper for localStorage data
  async migrateFromLocalStorage(): Promise<{ success: boolean; migrated: number; errors: string[] }> {
    const errors: string[] = []
    let migrated = 0

    try {
      // Migrate books index
      const booksIndexKey = 'books_index'
      const savedBooks = localStorage.getItem(booksIndexKey)

      if (savedBooks) {
        const books = JSON.parse(savedBooks)
        await this.putMany('books', books)
        migrated += books.length
        localStorage.removeItem(booksIndexKey)
      }

      // Migrate individual book data and chapters
      const localStorageKeys = Object.keys(localStorage)
      for (const key of localStorageKeys) {
        try {
          if (key.startsWith('book_')) {
            const bookData = JSON.parse(localStorage.getItem(key) || '{}')
            if (bookData.chapters) {
              // SECURITY FIX: Add book_id to chapters during migration
              const chaptersWithBookId = bookData.chapters.map((chapter: any) => ({
                ...chapter,
                book_id: chapter.book_id || bookData.id
              }))
              await this.putMany('chapters', chaptersWithBookId)
              migrated += chaptersWithBookId.length
            }
            localStorage.removeItem(key)
          } else if (key.startsWith('chapter_') && key.endsWith('_witnesses')) {
            const witnesses = JSON.parse(localStorage.getItem(key) || '[]')
            if (witnesses.length > 0) {
              // SECURITY FIX: Add book_id to witnesses during migration
              const chapterId = key.replace('chapter_', '').replace('_witnesses', '')
              const chapter = await this.get('chapters', chapterId)
              const witnessesWithBookId = witnesses.map((witness: any) => ({
                ...witness,
                book_id: witness.book_id || chapter?.book_id
              }))
              await this.putMany('witnesses', witnessesWithBookId)
              migrated += witnessesWithBookId.length
            }
            localStorage.removeItem(key)
          } else if (key.startsWith('chapter_') && key.endsWith('_alignments')) {
            const alignments = JSON.parse(localStorage.getItem(key) || '[]')
            if (alignments.length > 0) {
              // SECURITY FIX: Add book_id to alignments during migration
              const chapterId = key.replace('chapter_', '').replace('_alignments', '')
              const chapter = await this.get('chapters', chapterId)
              const alignmentsWithBookId = alignments.map((alignment: any) => ({
                ...alignment,
                book_id: alignment.book_id || chapter?.book_id
              }))
              await this.putMany('alignments', alignmentsWithBookId)
              migrated += alignmentsWithBookId.length
            }
            localStorage.removeItem(key)
          }
        } catch (error) {
          errors.push(`Failed to migrate ${key}: ${error}`)
        }
      }

      return { success: errors.length === 0, migrated, errors }
    } catch (error) {
      errors.push(`Migration failed: ${error}`)
      return { success: false, migrated, errors }
    }
  }

  // ============================================================================
  // EMERGENCY PERSISTENCE: Witness Chunk Results
  // ============================================================================

  async saveWitnessChunkResult(witnessId: string, runId: string, chunkId: string, result: any): Promise<void> {
    const id = `${witnessId}_${runId}_${chunkId}`
    const timestamp = Date.now()

    const chunkResult: DBSchema['witness_chunk_results'] = {
      id,
      witness_id: witnessId,
      run_id: runId,
      chunk_id: chunkId,
      result,
      created_at: timestamp,
      last_updated: timestamp,
      is_emergency_save: true,
      sync_status: 'pending'
    }

    await this.put('witness_chunk_results', chunkResult)
    console.log('💾 Emergency saved witness chunk result:', { witnessId, runId, chunkId })
  }

  async getWitnessChunkResults(witnessId: string, runId: string): Promise<Map<string, any>> {
    const results = await this.getAllByCompoundIndex('witness_chunk_results', 'witness_run', [witnessId, runId])
    const resultMap = new Map<string, any>()

    results.forEach(item => {
      resultMap.set(item.chunk_id, item.result)
    })

    console.log('📖 Loaded witness chunk results from IndexedDB:', { witnessId, runId, count: resultMap.size })
    return resultMap
  }

  async getAllWitnessRuns(witnessId: string): Promise<Map<string, Map<string, any>>> {
    const results = await this.getAllByIndex('witness_chunk_results', 'witness_id', witnessId)
    const runsMap = new Map<string, Map<string, any>>()

    results.forEach(item => {
      if (!runsMap.has(item.run_id)) {
        runsMap.set(item.run_id, new Map())
      }
      runsMap.get(item.run_id)!.set(item.chunk_id, item.result)
    })

    console.log('📖 Loaded all witness runs from IndexedDB:', { witnessId, runs: runsMap.size })
    return runsMap
  }

  async deleteWitnessChunkResult(witnessId: string, runId: string, chunkId: string): Promise<void> {
    const id = `${witnessId}_${runId}_${chunkId}`
    await this.delete('witness_chunk_results', id)
    console.log('🗑️ Deleted witness chunk result:', { witnessId, runId, chunkId })
  }

  async clearWitnessRun(witnessId: string, runId: string): Promise<void> {
    const deleted = await this.deleteMatching(
      'witness_chunk_results',
      result => result.witness_id === witnessId && result.run_id === runId,
    )
    console.log('🗑️ Cleared witness run:', { witnessId, runId, deleted })
  }

  // ============================================================================
  // WORKBENCH WITNESS LAYERS
  // ============================================================================

  async saveWitnessComparisonLayer(layer: WitnessComparisonLayer): Promise<void> {
    const existingLayers = await this.getAllByIndex('witness_comparison_layers', 'witness_id', layer.witness_id)
    const updates = existingLayers
      .filter(candidate => candidate.is_active && candidate.id !== layer.id)
      .map(candidate => ({ ...candidate, is_active: false }))

    const dataIntegrityHash = await this.generateDataIntegrityHash({
      witness_id: layer.witness_id,
      raw_text_hash: layer.raw_text_hash,
      cleaned_text: layer.cleaned_text,
      token_count: layer.token_count,
      artifact_summary: layer.artifact_summary
    })

    await this.putMany('witness_comparison_layers', [
      ...updates,
      {
        ...layer,
        is_active: true,
        data_integrity_hash: dataIntegrityHash
      }
    ])
  }

  async getActiveWitnessComparisonLayer(witnessId: string): Promise<WitnessComparisonLayer | null> {
    const layers = (await this.getAllByIndex('witness_comparison_layers', 'witness_id', witnessId))
      .filter(layer => layer.is_active)
    return layers.sort((left, right) => right.created_at - left.created_at)[0] || null
  }

  async getWitnessComparisonLayers(witnessId: string): Promise<WitnessComparisonLayer[]> {
    const layers = await this.getAllByIndex('witness_comparison_layers', 'witness_id', witnessId)
    return layers.sort((left, right) => right.created_at - left.created_at)
  }

  async saveWitnessSourceMap(sourceMap: WitnessSourceMap): Promise<void> {
    const existingMaps = await this.getAllByIndex('witness_source_maps', 'witness_id', sourceMap.witness_id)
    const updates = existingMaps
      .filter(candidate => candidate.is_active && candidate.id !== sourceMap.id)
      .map(candidate => ({ ...candidate, is_active: false }))

    const dataIntegrityHash = await this.generateDataIntegrityHash({
      witness_id: sourceMap.witness_id,
      raw_text_hash: sourceMap.raw_text_hash,
      line_count: sourceMap.line_count,
      token_count: sourceMap.token_count,
      foliation_markers: sourceMap.foliation_markers,
      lines: sourceMap.lines,
      tokens: sourceMap.tokens
    })

    await this.putMany('witness_source_maps', [
      ...updates,
      {
        ...sourceMap,
        is_active: true,
        data_integrity_hash: dataIntegrityHash
      }
    ])
  }

  async getActiveWitnessSourceMap(witnessId: string): Promise<WitnessSourceMap | null> {
    const sourceMaps = (await this.getAllByIndex('witness_source_maps', 'witness_id', witnessId))
      .filter(sourceMap => sourceMap.is_active)
    return sourceMaps.sort((left, right) => right.created_at - left.created_at)[0] || null
  }

  async getWitnessSourceMaps(witnessId: string): Promise<WitnessSourceMap[]> {
    const sourceMaps = await this.getAllByIndex('witness_source_maps', 'witness_id', witnessId)
    return sourceMaps.sort((left, right) => right.created_at - left.created_at)
  }

  async saveWorkbenchProject(project: WorkbenchProjectRecord): Promise<void> {
    const dataIntegrityHash = await this.generateDataIntegrityHash({
      id: project.id,
      name: project.name,
      description: project.description || '',
      source_book_ids: [...project.source_book_ids].sort(),
      build_targets: (project.build_targets || [])
        .map(target => `${target.work_id}:${target.context_id}:${target.dataset_name}`)
        .sort(),
      last_modified: project.last_modified
    })

    await this.put('workbench_projects', {
      ...project,
      data_integrity_hash: dataIntegrityHash
    })
  }

  async getWorkbenchProject(projectId: string): Promise<WorkbenchProjectRecord | null> {
    const project = await this.get('workbench_projects', projectId)
    return project || null
  }

  async getWorkbenchProjects(): Promise<WorkbenchProjectRecord[]> {
    const projects = await this.getAll('workbench_projects')
    return projects.sort((left, right) => left.created_at - right.created_at)
  }

  async saveSourceObject(sourceObject: SourceObject): Promise<void> {
    await this.put('source_objects', sourceObject)
  }

  async getSourceObject(sourceObjectId: string): Promise<SourceObject | null> {
    const sourceObject = await this.get('source_objects', sourceObjectId)
    return sourceObject || null
  }

  async getSourceObjects(): Promise<SourceObject[]> {
    return await this.getAll('source_objects')
  }

  async getSourceObjectsByWork(workId: string): Promise<SourceObject[]> {
    return await this.getAllByIndex('source_objects', 'work_id', workId)
  }

  async getSourceObjectByMatchKey(workId: string, matchKey: string): Promise<SourceObject | null> {
    const matches = await this.getAllByCompoundIndex('source_objects', 'work_match_key', [workId, matchKey])
    return matches[0] || null
  }

  async saveSourceDocument(document: SourceDocumentRecord): Promise<void> {
    await this.put('source_documents', document)
  }

  async getSourceDocument(documentId: string): Promise<SourceDocumentRecord | null> {
    const document = await this.get('source_documents', documentId)
    return document || null
  }

  async getSourceDocuments(): Promise<SourceDocumentRecord[]> {
    return await this.getAll('source_documents')
  }

  async getSourceDocumentsBySourceObject(sourceObjectId: string): Promise<SourceDocumentRecord[]> {
    return await this.getAllByIndex('source_documents', 'source_object_id', sourceObjectId)
  }

  async getSourceDocumentsByWork(workId: string): Promise<SourceDocumentRecord[]> {
    return await this.getAllByIndex('source_documents', 'work_id', workId)
  }

  async deleteSourceDocument(documentId: string): Promise<void> {
    await this.delete('source_documents', documentId)
  }

  async saveSectionOccurrence(occurrence: SourceSectionOccurrence): Promise<void> {
    await this.put('section_occurrences', occurrence)
  }

  async getSectionOccurrence(occurrenceId: string): Promise<SourceSectionOccurrence | null> {
    const occurrence = await this.get('section_occurrences', occurrenceId)
    return occurrence || null
  }

  async getSectionOccurrences(): Promise<SourceSectionOccurrence[]> {
    return await this.getAll('section_occurrences')
  }

  async getSectionOccurrencesByWorkSection(
    authorityWorkId: string,
    authoritySectionId: string
  ): Promise<SourceSectionOccurrence[]> {
    return await this.getAllByCompoundIndex('section_occurrences', 'work_section', [authorityWorkId, authoritySectionId])
  }

  async getSectionOccurrencesBySourceObject(sourceObjectId: string): Promise<SourceSectionOccurrence[]> {
    return await this.getAllByIndex('section_occurrences', 'source_object_id', sourceObjectId)
  }

  async deleteSectionOccurrence(occurrenceId: string): Promise<void> {
    await this.delete('section_occurrences', occurrenceId)
  }

  async saveSourceCoordinateImportReview(review: SourceCoordinateImportReview): Promise<void> {
    await this.put('source_coordinate_import_reviews', review)
  }

  async getSourceCoordinateImportReview(reviewId: string): Promise<SourceCoordinateImportReview | null> {
    const review = await this.get('source_coordinate_import_reviews', reviewId)
    return review || null
  }

  async getSourceCoordinateImportReviews(): Promise<SourceCoordinateImportReview[]> {
    const reviews = await this.getAll('source_coordinate_import_reviews')
    return reviews.sort((left, right) => right.created_at - left.created_at)
  }

  async deleteSourceCoordinateImportReview(reviewId: string): Promise<void> {
    await this.delete('source_coordinate_import_reviews', reviewId)
  }

  async saveManuscriptRecord(record: ManuscriptRecord): Promise<void> {
    await this.put('manuscript_records', record)
  }

  async getManuscriptRecord(recordId: string): Promise<ManuscriptRecord | null> {
    const record = await this.get('manuscript_records', recordId)
    return record || null
  }

  async getManuscriptRecords(): Promise<ManuscriptRecord[]> {
    return await this.getAll('manuscript_records')
  }

  async getManuscriptRecordByMatchKey(matchKey: string): Promise<ManuscriptRecord | null> {
    const matches = await this.getAllByIndex('manuscript_records', 'normalized_match_key', matchKey)
    return matches[0] || null
  }

  async getManuscriptRecordByManifestUrl(manifestUrl: string): Promise<ManuscriptRecord | null> {
    const matches = await this.getAllByIndex('manuscript_records', 'iiif_manifest_url', manifestUrl)
    return matches[0] || null
  }

  async deleteManuscriptRecord(recordId: string): Promise<void> {
    await this.delete('manuscript_records', recordId)
  }

  async saveAuthorityWork(work: AuthorityWork): Promise<void> {
    await this.put('authority_works', work)
  }

  async getAuthorityWork(workId: string): Promise<AuthorityWork | null> {
    const work = await this.get('authority_works', workId)
    return work || null
  }

  async getAuthorityWorks(): Promise<AuthorityWork[]> {
    return await this.getAll('authority_works')
  }

  async deleteAuthorityWork(workId: string): Promise<void> {
    await this.delete('authority_works', workId)
  }

  async getUiPreference(key: string): Promise<string> {
    if (!key) {
      return ''
    }
    const record = await this.get('ui_preferences', key)
    return record?.value || ''
  }

  async setUiPreference(key: string, value: string): Promise<void> {
    if (!key) {
      return
    }
    const existing = await this.get('ui_preferences', key)
    if (existing?.value === value) {
      return
    }
    await this.put('ui_preferences', {
      key,
      value,
      updated_at: Date.now()
    })
  }

  async saveWorkbenchWitnessAssignment(assignment: WorkbenchWitnessAssignmentRecord): Promise<void> {
    const dataIntegrityHash = await this.generateDataIntegrityHash({
      id: assignment.id,
      project_id: assignment.project_id,
      witness_id: assignment.witness_id,
      source_book_id: assignment.source_book_id,
      work_id: assignment.work_id || '',
      context_id: assignment.context_id || '',
      status: assignment.status,
      last_modified: assignment.last_modified
    })

    await this.put('workbench_witness_assignments', {
      ...assignment,
      data_integrity_hash: dataIntegrityHash
    })
  }

  async getWorkbenchWitnessAssignment(assignmentId: string): Promise<WorkbenchWitnessAssignmentRecord | null> {
    const assignment = await this.get('workbench_witness_assignments', assignmentId)
    return assignment || null
  }

  async getWorkbenchWitnessAssignments(projectId?: string): Promise<WorkbenchWitnessAssignmentRecord[]> {
    const assignments = projectId
      ? await this.getAllByIndex('workbench_witness_assignments', 'project_id', projectId)
      : await this.getAll('workbench_witness_assignments')
    return assignments.sort((left, right) => left.created_at - right.created_at)
  }

  async saveChapterDeviceLayer(layer: ChapterDeviceLayer): Promise<void> {
    await this.put('chapter_device_layers', layer)
  }

  async getChapterDeviceLayer(chapterId: string): Promise<ChapterDeviceLayer | undefined> {
    return this.get('chapter_device_layers', chapterId)
  }

  async saveWitnessDeviceLayer(layer: WitnessDeviceLayerRecord): Promise<void> {
    await this.put('witness_device_layers', layer)
  }

  async getWitnessDeviceLayer(witnessId: string): Promise<WitnessDeviceLayerRecord | undefined> {
    return this.get('witness_device_layers', witnessId)
  }

  async saveDeviceAnnotationRun(run: DeviceAnnotationRunRecord): Promise<void> {
    await this.put('device_annotation_runs', run)
  }

  async getDeviceAnnotationRun(runId: string): Promise<DeviceAnnotationRunRecord | undefined> {
    return this.get('device_annotation_runs', runId)
  }

  async listDeviceAnnotationRunsForChapter(chapterId: string): Promise<DeviceAnnotationRunRecord[]> {
    const runs = await this.getAllByIndex('device_annotation_runs', 'chapter_id', chapterId)
    return runs.sort((left, right) => left.created_at.localeCompare(right.created_at))
  }

  async saveChapterCharacterLayer(layer: ChapterCharacterLayer): Promise<void> {
    await this.put('chapter_character_layers', layer)
  }

  async getChapterCharacterLayer(chapterId: string): Promise<ChapterCharacterLayer | undefined> {
    return this.get('chapter_character_layers', chapterId)
  }

  async saveAnalyticalTaskSet(taskSet: AnalyticalTaskSet): Promise<void> {
    await this.put('analytical_task_sets', taskSet)
  }

  async getAnalyticalTaskSet(taskSetId: string): Promise<AnalyticalTaskSet | undefined> {
    return this.get('analytical_task_sets', taskSetId)
  }

  async listAnalyticalTaskSetsForWitness(witnessId: string): Promise<AnalyticalTaskSet[]> {
    return this.getAllByIndex('analytical_task_sets', 'witnessIds', witnessId)
  }

  async saveTranslationPreviewResume(record: TranslationPreviewResumeRecord): Promise<void> {
    await this.put('translation_preview_resumes', record)
  }

  async getTranslationPreviewResume(id: string): Promise<TranslationPreviewResumeRecord | undefined> {
    return this.get('translation_preview_resumes', id)
  }

  async listTranslationPreviewResumesForContext(
    surface: TranslationPreviewResumeSurface,
    contextId: string,
  ): Promise<TranslationPreviewResumeRecord[]> {
    return this.getAllByCompoundIndex('translation_preview_resumes', 'surface_context', [surface, contextId]) as Promise<TranslationPreviewResumeRecord[]>
  }

  async deleteTranslationPreviewResume(id: string): Promise<void> {
    await this.delete('translation_preview_resumes', id)
  }

  async deleteTranslationPreviewResumesForContext(
    surface: TranslationPreviewResumeSurface,
    contextId: string,
  ): Promise<void> {
    const records = await this.listTranslationPreviewResumesForContext(surface, contextId)
    await Promise.all(records.map(record => this.deleteTranslationPreviewResume(record.id)))
  }
}

// Create singleton instance
export const dbStorage = new IndexedDBStorage()

let persistentStorageRequest: Promise<boolean> | null = null

export const requestPersistentBrowserStorage = async (): Promise<boolean> => {
  if (persistentStorageRequest) {
    return persistentStorageRequest
  }
  persistentStorageRequest = (async () => {
    if (typeof navigator === 'undefined' || typeof navigator.storage?.persist !== 'function') {
      return false
    }
    try {
      if (typeof navigator.storage.persisted === 'function' && await navigator.storage.persisted()) {
        return true
      }
      return await navigator.storage.persist()
    } catch (error) {
      console.warn('Persistent browser storage could not be requested:', error)
      return false
    }
  })()
  return persistentStorageRequest
}

// Initialize on module load
export const initializeDB = async (): Promise<void> => {
  try {
    await dbStorage.init()
    void requestPersistentBrowserStorage()
    console.log('IndexedDB initialized successfully')
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error)
    throw error
  }
}
